/**
 * PSP/CRE Tool v2.0 - Unit Types Constants
 * 
 * Purpose: Define unit types, battalion compositions, casualty modifiers,
 * and tactical defaults for military medical planning calculations.
 * 
 * Author: Dr. Tatar Radu MD
 * Date: February 11, 2026
 * Version: 2.12.1 (March 24, 2026):
 *   - MEDICAL and MEDICAL_SUPPORT battalion descriptions clarified.
 *     MEDICAL: explicitly states all-NATO validity (Romanian/German/French/US organic
 *     medical companies all represented), distinguishes TREATS vs SUPPLIES.
 *     MEDICAL_SUPPORT: explicitly states logistics-only, no treatment, division/corps
 *     level guidance, warns against brigade-level misuse.
 *
 * Version: 2.12 (March 24, 2026):
 *   - BUG-182 FIX (data): Added AIRBORNE, AIR_ASSAULT, MOUNTAIN to BATTALION_TYPES.
 *     These existed only in UNIT_TYPES (brigade level) — an AIRBORNE brigade's
 *     battalions had no matching BATTALION_TYPES entry, falling back to MECHANIZED
 *     (wrong modifier, wrong position). Values confirmed by Dr. Tatar Radu MD:
 *     AIRBORNE 1.3x/600pax/2km, AIR_ASSAULT 1.2x/650pax/2km, MOUNTAIN 1.15x/700pax/3km.
 *     All three: has_frontage=true, needs_role1=true, COMBAT_ENGAGED.
 *
 * Version: 2.11 (March 23, 2026):
 *   - DOCTRINAL-005: CAVALRY renamed to RECONNAISSANCE throughout. France has
 *     Armée blindée et cavalerie (Hussards, Spahis, Dragons) performing recon;
 *     Romania has Cercetare battalions. RECONNAISSANCE is the NATO-generic
 *     functional name covering all nations in MND scenarios.
 *   - DOCTRINAL-006: Battalion HQ positions corrected per AJMedP-1.
 *     INFANTRY 2→5km, MECHANIZED 3→6km, ARMOR 4→8km.
 *     Previous values reflected TAC HQ forward positions, not battalion main HQ.
 *
 * Version: 2.10 - UT-GAP-003: doctrinal_composition added to AIR_ASSAULT, STRYKER,
 *   AVIATION, AIR_DEFENSE, CAVALRY, HQ (March 16, 2026).
 *   These 6 types route through generateSubUnitsFromDoctrine() and were at risk of
 *   hard throw "missing doctrinal_composition field" if built via ForceBuilder.
 *   ENGINEER/ENGINEERS/SIGNAL: routed through CSS_BRIGADE_TEMPLATES — no dc needed.
 *   MEDICAL: routed through isMedicalBrigade() — no dc needed.
 *   AIR_ASSAULT: 3 inf bn + 1 aviation + 1 artillery + 1 support + 1 cavalry + HQ = 4500.
 *     Source: 101st Airborne Division (Air Assault); 16 Air Assault Bde (UK).
 *   STRYKER: 3 inf bn (Stryker) + 1 artillery + 1 engineers + 1 BSB + 1 cavalry + HQ = 4500.
 *     Source: US Army SBCT organization (FM 3-21.31).
 *   AVIATION: 3 attack/assault bn + 1 general support + 1 ASB + HQ = 3000.
 *     Source: US CAB organization (FM 3-04).
 *   AIR_DEFENSE: 3 ADA bn + 1 maintenance + HQ = 2500. Source: FM 3-01.
 *   CAVALRY: 3 sqn + 1 artillery + 1 support + HQ = 3500. Source: FM 3-20.
 *   HQ: command, staff, signal, security = 2000. Source: ADP 6-0.
 *
 * Version: 2.9 - doctrinal_composition added to HEAVY, AIRBORNE, MOUNTAIN (March 15, 2026)
 *   BUG FIX: HEAVY, AIRBORNE, MOUNTAIN lacked doctrinal_composition — ForceService
 *     generateSubUnitsFromDoctrine() throws "missing doctrinal_composition field" if
 *     any of these types are built via ForceBuilder. Affected all nations (ROU, NATO,
 *     FRENCH, etc.) that include these types in available_brigade_types[].
 *   HEAVY: NATO 3-battalion armored brigade (2 ARMOR + 1 MECHANIZED + ARTILLERY +
 *     ENGINEERS + SUSTAINMENT + CAVALRY + HQ). Mirrors ARMOR doctrinal_composition
 *     (same ABCT structure — HEAVY is the generic, ARMOR is the US-specific alias).
 *     Source: FM 3-96 BCT Organization; NATO heavy brigade doctrine.
 *   AIRBORNE: 3-battalion light airborne brigade (3 INFANTRY + ARTILLERY + LOGISTICS
 *     + CAVALRY + HQ). Air-portable; no tracked assets; lighter footprint.
 *     Source: FM 3-21.10 (Airborne/Air Assault); 11e BP (French) / 82nd ABN (US).
 *   MOUNTAIN: 3-battalion mountain infantry brigade (3 INFANTRY + ARTILLERY + ENGINEERS
 *     + HQ). Lighter than standard infantry; animal transport; altitude medicine.
 *     Source: FM 3-21.10 mountain variant; 27e BIM (French) / Gebirgsjäger (German).
 * Version: 2.8 - BUG FIX: Negative typical_position_km sign errors
 *
 * v2.8 CHANGES (February 11, 2026):
 * - BUG FIX: 5 unit types had negative typical_position_km (sign errors from old convention)
 *   Convention is positive = behind FEBA (friendly side) per v2 docs
 *   Fixed: AVIATION battalion (-25→25), ARTILLERY brigade (-15→20),
 *          AVIATION brigade (-25→25), LOGISTICS brigade (-40→40), MEDICAL brigade (-30→30)
 * - These negatives were causing corrupted HQ positions for aviation/CSS/medical units
 *   whenever any code path fell through to typical_position_km instead of getBrigadeHQPosition
 *
 * Version: 2.7 - Remove Invalid 'OFFENSIVE' Posture
 * 
 * v2.7 CRITICAL BUG FIX (February 5, 2026):
 * - BUG FIX: Removed invalid 'OFFENSIVE' posture from all constraint arrays
 * - ISSUE: 'OFFENSIVE' was in OPERATIONAL_CONSTRAINTS but NOT in postures.js
 * - RESULT: MUI dropdown showed empty when translatePostureForUnitType returned 'OFFENSIVE'
 * - SYMPTOM: Infantry/Armored brigades had blank operational posture after unit type change
 * - ROOT CAUSE: 'OFFENSIVE' was documentation artifact, not actual military posture
 * - FIX 1: Removed 'OFFENSIVE' from COMBAT.allowed_postures (line 3087)
 * - FIX 2: Removed 'OFFENSIVE' from SUPPORT.allowed_postures (line 3110)
 * - FIX 3: Removed 'OFFENSIVE' from combatPostures translation array (line 3264)
 * - BEHAVIOR: Now returns 'DELIBERATE_ATTACK' as first valid offensive posture
 * - DOCTRINE: Actual offensive postures are DELIBERATE_ATTACK, HASTY_ATTACK, etc.
 * - IMPACT: Fixes empty operational posture bug when changing unit types
 * 
 * v2.6 LABEL CLEANUP (January 30, 2026):
 * - Removed "Brigade" word from all UNIT_TYPES labels for cleaner UI
 * - "Mechanized Brigade" → "Mechanized"
 * - "Armored Brigade" → "Armored"
 * - More flexible for future battalion/division level use
 * - Context (Tab/Dialog) makes echelon clear
 * - BATTALION_TYPES unchanged (already correctly specify "Battalion")
 * - 100% backward compatible - only cosmetic label changes
 * 
 * v2.5 BUG FIXES (January 20, 2026):
 * 
 * PART 1: BATTALION_COMPOSITION_BY_BRIGADE_TYPE fixes
 * - BUG-008: Added ARMOR brigade template (was missing, fell back to DEFAULT)
 * - BUG-009: Added STRYKER brigade template (US Army SBCT)
 * - BUG-010: Added ENGINEER/ENGINEERS brigade template
 * - BUG-011: Added SIGNAL brigade template  
 * - BUG-012: Added AVIATION brigade template (Combat Aviation Brigade)
 * - BUG-013: Added AIR_DEFENSE brigade template (ADA Brigade)
 * - BUG-014: Added CAVALRY brigade template (Armored Cavalry Regiment)
 * 
 * PART 2: UNIT_TYPES fixes (for casualty calculator!)
 * The calculator uses getCasualtyModifier(unitType) which looks up in UNIT_TYPES.
 * These brigade types existed in BATTALION_COMPOSITION but NOT in UNIT_TYPES!
 * - BUG-015: Added ARMOR to UNIT_TYPES (casualty_modifier: 0.95)
 * - BUG-016: Added STRYKER to UNIT_TYPES (casualty_modifier: 1.05)
 * - BUG-017: Added ENGINEER/ENGINEERS to UNIT_TYPES (casualty_modifier: 0.5)
 * - BUG-018: Added SIGNAL to UNIT_TYPES (casualty_modifier: 0.25)
 * - BUG-019: Added AIR_DEFENSE to UNIT_TYPES (casualty_modifier: 0.35)
 * - BUG-020: Added CAVALRY to UNIT_TYPES (casualty_modifier: 0.85)
 * - BUG-021: Added SUSTAINMENT to UNIT_TYPES (casualty_modifier: 0.3)
 * 
 * PART 3: Position and Frontage (used by ForceBuilder)
 * Each new UNIT_TYPES entry includes:
 * - has_frontage: Whether unit holds FEBA frontage (affects casualty calc)
 * - typical_position_km: Distance from FEBA (positive = behind, negative = forward)
 * - default_posture / operational_state: How unit typically operates
 * 
 * Without these fixes:
 * - Brigade types fell back to DEFAULT battalion composition (3×Infantry)
 * - Calculator returned 1.0 casualty modifier for unknown types
 * - ForceBuilder positioned all units at 8km (default) regardless of type
 * 
 * v2.2 CHANGES (CSS Hybrid Approach):
 * - CSS-001: Added SUSTAINMENT brigade template (US-style)
 * - CSS-002: Fixed LOGISTICS brigade template (ROU/NATO style with TRANSPORT, SUPPLY)
 * - CSS-003: Added new battalion types: TRANSPORT, SUPPLY, MEDICAL_SUPPORT
 * - Updated LOGISTICS battalion needs_role1 = true (soldiers need medical care)
 * - Both LOGISTICS and SUSTAINMENT available for multinational operations
 * - National standards control which types appear in dropdown
 * 
 * v2.1 CHANGES (Phase 1 - UT-001 Fix):
 * - Added needs_role1 flag to battalion types to separate medical org from frontage
 * - ARTILLERY, ENGINEERS, AIR_DEFENSE: needs_role1 = true (need organic Role 1)
 * - SIGNAL, LOGISTICS, MAINTENANCE, MEDICAL: needs_role1 = false (pure support)
 * - Updated ARTILLERY template with correct combatBattalions count
 * - Fixed typical_position_km signs (positive = behind FEBA)
 * 
 * References:
 * - NATO ATP 3.2.1 Land Tactics
 * - FM 3-90 Tactics
 * - Ukraine 2022-2024 Combat Data (RUSI, Mediazona, CSIS)
 * - Romanian Army Doctrine
 * 
 * Key Concepts:
 * - Unit types determine base casualty modifiers
 * - needs_role1 determines if battalion needs organic Role 1 (separate from has_frontage)
 * - Drone/artillery vulnerability varies significantly by unit type
 * - Modern warfare patterns differ from historical data
 * - Medical support requirements scale with risk
 * 
 * Usage:
import { 
 *   UNIT_TYPES,
 *   BATTALION_TYPES,
 *   getUnitType,
 *   getCasualtyModifier,
 *   calculateUnitCasualties
 * } from '../constants/unitTypes';
 */

// ============================================================================
// OPERATIONAL STATES IMPORT
// ============================================================================

import { lookupByUnitType } from './unitTypeRegistry';
import { 
  OPERATIONAL_STATES,
  getDefaultStateForUnitType,
  getBattleCasualtyMultiplier,
  getDNBIRate,
  getOperationalStateOptions
} from './operationalStates';

// Re-export for backward compatibility
export { 
  OPERATIONAL_STATES,
  getDefaultStateForUnitType,
  getBattleCasualtyMultiplier,
  getDNBIRate,
  getOperationalStateOptions
};

// ============================================================================
// BATTALION TYPES
// Used for battalion-level unit type selection
// ============================================================================

export const BATTALION_TYPES = {
  // -------------------------------------------------------------------------
  // COMBAT/MANEUVER TYPES (High engagement, high casualties)
  // -------------------------------------------------------------------------
  INFANTRY: {
    value: 'INFANTRY',
    label: 'Infantry Battalion',
    icon: '🎖️',
    category: 'COMBAT',
    
    // Casualty modifiers
    casualty_modifier: 1.2,
    
    // Ukraine-validated casualty data
    ukraine_data: {
      base_rate: 1.25,              // 25% higher than baseline
      artillery_vulnerability: 1.4,  // Very vulnerable to artillery
      drone_vulnerability: 1.2,      // Moderately vulnerable to drones
      mine_vulnerability: 1.5,       // High mine casualties
      
      wia_kia_ratio: 3.5,           // Higher survival due to lighter wounds
      casualty_notes: 'Highest absolute casualties in Ukraine'
    },
    
    // Position and frontage
    has_frontage: true,
    needs_role1: true,              // BUG-177 FIX: combat types need organic Role 1
    typical_position_km: 5,   // AJMedP-1: battalion main HQ 5-8km from FEBA (was 2 - TAC HQ distance)
    frontage_km: { min: 2, max: 5 },
    
    // Operational defaults
    operational_state: 'COMBAT_ENGAGED',
    default_posture: 'HASTY_DEFENSE',
    default_mission: 'DEFENSE',
    
    // Strength
    typical_strength: 800,
    strength_range: { min: 500, max: 1000 },
    
    // Medical requirements (enhanced)
    medical: {
      medics_per_company: 6,
      aid_station_staff: 8,
      ambulances: 4,
      enhanced_ifak: true,
      
      expected_daily_casualties: {
        low: 2,
        moderate: 8,
        high: 25,
        extreme: 50
      }
    },
    
    // Modern warfare modifiers
    modern_warfare: {
      drone_strike_risk: 'HIGH',
      artillery_exposure: 'VERY_HIGH',
      electronic_signature: 'LOW',
      thermal_signature: 'MODERATE'
    },
    
    description: 'Dismounted infantry - highest casualties in close combat',
    educational_note: 'Infantry units have highest casualty rates but also highest WIA:KIA ratios'
  },
  
  MECHANIZED: {
    value: 'MECHANIZED',
    label: 'Mechanized Infantry Battalion',
    icon: '🚜',
    category: 'COMBAT',
    
    casualty_modifier: 1.0,
    
    ukraine_data: {
      base_rate: 1.0,               // Baseline
      artillery_vulnerability: 1.2,
      drone_vulnerability: 1.3,      // Vehicle detection easier
      mine_vulnerability: 1.2,
      
      wia_kia_ratio: 3.0,           // Vehicle protection helps
      vehicle_loss_rate: 0.15,      // 15% vehicles per major engagement
      casualty_notes: 'Vehicle protection significant but not absolute'
    },
    
    has_frontage: true,
    needs_role1: true,              // BUG-177 FIX: combat types need organic Role 1
    typical_position_km: 6,   // AJMedP-1: mechanized battalion HQ (was 3 - TAC HQ distance)
    frontage_km: { min: 3, max: 6 },
    
    operational_state: 'COMBAT_ENGAGED',
    default_posture: 'DELIBERATE_DEFENSE',
    default_mission: 'DEFENSE',
    
    typical_strength: 800,
    strength_range: { min: 600, max: 1000 },
    
    medical: {
      medics_per_company: 5,
      aid_station_staff: 8,
      ambulances: 4,
      tracked_ambulances: 2,        // Track-mounted for mobility
      
      expected_daily_casualties: {
        low: 2,
        moderate: 6,
        high: 20,
        extreme: 40
      }
    },
    
    modern_warfare: {
      drone_strike_risk: 'HIGH',    // Vehicles easy to spot
      artillery_exposure: 'HIGH',
      electronic_signature: 'HIGH',
      thermal_signature: 'VERY_HIGH'
    },
    
    description: 'Infantry with IFV/APC protection',
    educational_note: 'Vehicles provide protection but attract fire and drones'
  },
  
  ARMOR: {
    value: 'ARMOR',
    label: 'Armor/Tank Battalion',
    icon: '🛡️',
    category: 'COMBAT',
    
    casualty_modifier: 0.85,
    
    ukraine_data: {
      base_rate: 0.80,              // Lower casualties per engagement
      artillery_vulnerability: 0.9,  // Armor provides some protection
      drone_vulnerability: 1.5,      // VERY high - FPV drones devastating
      mine_vulnerability: 0.8,       // Tracks trigger but crew often survives
      atgm_vulnerability: 1.8,       // Very high ATGM casualties
      
      wia_kia_ratio: 2.0,           // Tank kills often catastrophic
      vehicle_loss_rate: 0.25,      // High vehicle loss rate
      casualty_notes: 'FPV drones and ATGMs cause catastrophic kills'
    },
    
    has_frontage: true,
    needs_role1: true,              // BUG-177 FIX: combat types need organic Role 1
    typical_position_km: 8,   // AJMedP-1: armor battalion HQ (was 4 - TAC HQ distance)
    frontage_km: { min: 3, max: 8 },
    
    operational_state: 'COMBAT_ENGAGED',
    default_posture: 'DELIBERATE_DEFENSE',
    default_mission: 'DEFENSE',
    
    typical_strength: 800,
    strength_range: { min: 400, max: 1000 },
    
    medical: {
      medics_per_company: 4,
      aid_station_staff: 6,
      ambulances: 3,
      burn_treatment_priority: true, // Tank fires cause burns
      
      expected_daily_casualties: {
        low: 1,
        moderate: 5,
        high: 15,
        extreme: 35
      }
    },
    
    modern_warfare: {
      drone_strike_risk: 'EXTREME', // Primary FPV target
      artillery_exposure: 'MODERATE',
      electronic_signature: 'VERY_HIGH',
      thermal_signature: 'EXTREME'
    },
    
    description: 'Tank battalions - high protection but drone-vulnerable',
    educational_note: 'Lower personnel casualties but high vehicle losses; burns common'
  },
  
  RECONNAISSANCE: {
    value: 'RECONNAISSANCE',
    label: 'Reconnaissance Battalion',
    icon: '🔭',
    category: 'COMBAT',
    
    casualty_modifier: 1.1,
    
    ukraine_data: {
      base_rate: 1.15,
      artillery_vulnerability: 1.0,
      drone_vulnerability: 1.4,     // Forward position = exposed
      mine_vulnerability: 1.6,      // Lead elements hit mines
      
      wia_kia_ratio: 3.2,
      casualty_notes: 'High risk from leading position and mine encounters'
    },
    
    has_frontage: true,
    needs_role1: true,              // BUG-177 FIX: combat types need organic Role 1
    typical_position_km: 0,         // Forward of FEBA
    frontage_km: { min: 5, max: 15 },
    
    operational_state: 'COMBAT_ENGAGED',
    default_posture: 'MOVEMENT_TO_CONTACT',
    default_mission: 'RECONNAISSANCE',
    
    typical_strength: 600,
    strength_range: { min: 400, max: 800 },
    
    medical: {
      medics_per_company: 4,
      aid_station_staff: 6,
      ambulances: 3,
      forward_aid_capability: true,
      
      expected_daily_casualties: {
        low: 1,
        moderate: 5,
        high: 18,
        extreme: 40
      }
    },
    
    modern_warfare: {
      drone_strike_risk: 'HIGH',
      artillery_exposure: 'HIGH',
      electronic_signature: 'MODERATE',
      thermal_signature: 'HIGH'
    },
    
    description: 'Reconnaissance and screening - forward exposure',

    // -------------------------------------------------------------------------
    // DOCTRINAL COMPOSITION (v2.10 - UT-GAP-003)
    // Cavalry / Reconnaissance Brigade — screening, security, intelligence
    // Source: FM 3-20 Reconnaissance and Security; US ACR/RSTA organization
    // -------------------------------------------------------------------------
    doctrinal_composition: {
      source: 'NATO Cavalry / Reconnaissance Brigade Organization (generic)',
      reference: 'FM 3-20; ATP 3-20.15; US ACR MTOE; NATO recce brigade',

      brigade: {
        total_strength: 3500,
        sub_units: [
          {
            type: 'SQUADRON',
            unit_type: 'RECONNAISSANCE',
            count: 3,
            strength_each: 800,
            name_pattern: '{parent} Cavalry Squadron {index}',
            notes: 'Combined arms cavalry squadrons — Stryker/wheeled recce or tank/mech mix'
          },
          {
            type: 'BATTALION',
            unit_type: 'ARTILLERY',
            count: 1,
            strength_each: 450,
            name_pattern: '{parent} Artillery Battalion',
            notes: 'Direct support artillery — fires in support of recce screen'
          },
          {
            type: 'BATTALION',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 400,
            name_pattern: '{parent} Support Battalion',
            notes: 'Brigade CSS — fuel-intensive for high-tempo recce operations'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 150,
            name_pattern: '{parent} Headquarters Troop',
            notes: 'Brigade HQ — reconnaissance coordination and reporting'
          }
        ]
      },

      battalion: {
        total_strength: 800,
        sub_units: [
          {
            type: 'TROOP',
            unit_type: 'RECONNAISSANCE',
            count: 3,
            strength_each: 180,
            name_pattern: '{parent} Troop {letter}',
            notes: 'Cavalry troops — 9–12 recce vehicles each'
          },
          {
            type: 'TROOP',
            unit_type: 'ARMOR',
            count: 1,
            strength_each: 150,
            name_pattern: '{parent} Tank Troop',
            notes: 'Tank troop — direct fire support for screening missions'
          },
          {
            type: 'TROOP',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Troop',
            notes: 'Squadron HQ and forward support element'
          },
          {
            type: 'COMPANY',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 120,
            name_pattern: '{parent} Forward Support Troop',
            notes: 'Fuel, ammunition, maintenance for high-tempo recce'
          }
        ]
      }
    },

    educational_note: 'High mine casualties from leading tactical movements'
  },
  
  // -------------------------------------------------------------------------
  // COMBAT SUPPORT TYPES (Moderate engagement, moderate casualties)
  // -------------------------------------------------------------------------
  ARTILLERY: {
    value: 'ARTILLERY',
    label: 'Artillery Battalion',
    icon: '💥',
    category: 'SUPPORT',
    
    casualty_modifier: 0.4,   // 2026-10-11: was 0.6 (rear exposure scaled by about 0.7, Dr. Tatar; EDUCATED GUESS)
    
    ukraine_data: {
      base_rate: 0.7,
      artillery_vulnerability: 1.8,  // Counter-battery fire devastating
      drone_vulnerability: 1.6,      // High-value drone targets
      mine_vulnerability: 0.3,       // Rear positions
      
      wia_kia_ratio: 2.8,
      casualty_notes: 'Counter-battery and drone strikes primary threat'
    },
    
    has_frontage: false,            // Does NOT hold FEBA frontage (for casualty calc)
    needs_role1: true,              // v2.4.0: DOES need organic Role 1 medical support
    typical_position_km: 18,        // Behind FEBA (positive = friendly side)
    
    operational_state: 'SUPPORT_ACTIVE',
    default_posture: 'DELIBERATE_DEFENSE',
    default_mission: 'SUPPORT',
    
    typical_strength: 500,
    strength_range: { min: 300, max: 700 },
    
    medical: {
      medics_per_battery: 3,
      aid_station_staff: 6,
      ambulances: 2,
      hearing_protection_priority: true,
      
      expected_daily_casualties: {
        low: 0,
        moderate: 2,
        high: 8,
        extreme: 20
      }
    },
    
    modern_warfare: {
      drone_strike_risk: 'EXTREME', // Primary Lancet/FPV target
      artillery_exposure: 'EXTREME', // Counter-battery
      electronic_signature: 'HIGH',
      thermal_signature: 'EXTREME', // Guns fire = hot
      
      shoot_and_scoot_critical: true,
      survival_time_static_min: 3   // Must move within 3 min of firing
    },
    
    description: 'Field artillery - counter-battery primary threat',
    educational_note: 'Ukraine: Must relocate within 3 minutes of firing'
  },
  
  ENGINEERS: {
    value: 'ENGINEERS',
    label: 'Engineer Battalion',
    icon: '🔧',
    category: 'SUPPORT',
    
    casualty_modifier: 0.8,
    
    ukraine_data: {
      base_rate: 0.85,
      artillery_vulnerability: 1.0,
      drone_vulnerability: 1.3,      // Working parties visible
      mine_vulnerability: 2.0,       // Breaching = mine exposure
      
      wia_kia_ratio: 3.0,
      casualty_notes: 'Mine/IED casualties primary threat'
    },
    
    has_frontage: true,
    needs_role1: true,              // v2.4.0: Combat engineers need Role 1
    typical_position_km: 5,         // Near FEBA for breaching ops
    
    operational_state: 'COMBAT_ENGAGED',   // 2026-10-17: engineers are frontline (decision 2026-10-11, has_frontage true); was SUPPORT_ACTIVE, which contradicted it
    default_posture: 'HASTY_DEFENSE',
    default_mission: 'SUPPORT',
    
    typical_strength: 600,
    strength_range: { min: 400, max: 800 },
    
    medical: {
      medics_per_company: 4,
      aid_station_staff: 6,
      ambulances: 3,
      
      expected_daily_casualties: {
        low: 0,
        moderate: 3,
        high: 12,
        extreme: 30
      }
    },
    
    modern_warfare: {
      drone_strike_risk: 'HIGH',     // Construction visible
      artillery_exposure: 'MODERATE',
      electronic_signature: 'MODERATE',
      thermal_signature: 'MODERATE'
    },
    
    description: 'Combat engineers - mine/IED primary threat',
    educational_note: 'Breaching operations extremely hazardous'
  },
  
  AIR_DEFENSE: {
    value: 'AIR_DEFENSE',
    label: 'Air Defense Battalion',
    icon: '🚀',
    category: 'SUPPORT',
    
    casualty_modifier: 0.35,   // 2026-10-11: was 0.5 (rear exposure scaled by about 0.7, Dr. Tatar; EDUCATED GUESS)
    
    ukraine_data: {
      base_rate: 0.6,
      artillery_vulnerability: 1.2,
      drone_vulnerability: 1.4,      // High-value targets
      sead_vulnerability: 2.0,       // SEAD/DEAD missions target
      
      wia_kia_ratio: 2.5,
      casualty_notes: 'High-value target for enemy SEAD'
    },
    
    has_frontage: false,
    needs_role1: true,              // v2.4.0: ADA units need Role 1
    typical_position_km: 10,        // Covering forward units
    
    operational_state: 'SUPPORT_ACTIVE',
    default_posture: 'DELIBERATE_DEFENSE',
    default_mission: 'SUPPORT',
    
    typical_strength: 400,
    strength_range: { min: 250, max: 600 },
    
    medical: {
      medics_per_battery: 2,
      aid_station_staff: 4,
      ambulances: 2,
      
      expected_daily_casualties: {
        low: 0,
        moderate: 1,
        high: 5,
        extreme: 15
      }
    },
    
    modern_warfare: {
      drone_strike_risk: 'EXTREME',  // Lancet primary target
      artillery_exposure: 'HIGH',
      electronic_signature: 'EXTREME', // Radar emissions
      thermal_signature: 'HIGH'
    },
    
    description: 'Air defense - SEAD/drone priority targets',

    // -------------------------------------------------------------------------
    // DOCTRINAL COMPOSITION (v2.10 - UT-GAP-003)
    // Air Defense Brigade — SHORAD/HIMAD protection of force and key assets
    // Source: FM 3-01 Air and Missile Defense Operations; NATO ADA brigade
    // -------------------------------------------------------------------------
    doctrinal_composition: {
      source: 'NATO Air Defense Brigade Organization (generic)',
      reference: 'FM 3-01; ATP 3-01.8; NATO IAMD doctrine',

      brigade: {
        total_strength: 2500,
        sub_units: [
          {
            type: 'BATTALION',
            unit_type: 'AIR_DEFENSE',
            count: 3,
            strength_each: 550,
            name_pattern: '{parent} Air Defense Battalion {index}',
            notes: 'Mixed SHORAD/HIMAD battalions — Patriot/NASAMS/HAWK or equivalent'
          },
          {
            type: 'BATTALION',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 350,
            name_pattern: '{parent} Maintenance Battalion',
            notes: 'ADA maintenance and supply — radar/missile system support'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 150,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Brigade ADA operations center — airspace coordination'
          }
        ]
      },

      battalion: {
        total_strength: 550,
        sub_units: [
          {
            type: 'BATTERY',
            unit_type: 'AIR_DEFENSE',
            count: 3,
            strength_each: 130,
            name_pattern: '{parent} Battery {letter}',
            notes: 'ADA firing batteries — 2–4 launchers each'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Battery',
            notes: 'Battalion fire control, radar section, comms'
          },
          {
            type: 'COMPANY',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 60,
            name_pattern: '{parent} Support Battery',
            notes: 'Maintenance and supply for ADA systems'
          }
        ]
      }
    },

    educational_note: 'High-value targets; must maintain EMCON'
  },
  
  SIGNAL: {
    value: 'SIGNAL',
    label: 'Signal Battalion',
    icon: '📡',
    category: 'SUPPORT',
    
    casualty_modifier: 0.3,   // 2026-10-11: was 0.4 (rear exposure scaled by about 0.7, Dr. Tatar; EDUCATED GUESS)
    
    ukraine_data: {
      base_rate: 0.45,
      artillery_vulnerability: 0.8,
      drone_vulnerability: 1.2,
      electronic_attack: 1.5,        // EW targeting
      
      wia_kia_ratio: 3.5,
      casualty_notes: 'EW targeting based on emissions'
    },
    
    has_frontage: false,
    needs_role1: false,             // v2.4.0: Pure support, no organic Role 1
    typical_position_km: 20,        // Behind main body
    
    operational_state: 'SUPPORT_ACTIVE',
    default_posture: 'DELIBERATE_DEFENSE',
    default_mission: 'SUPPORT',
    
    typical_strength: 400,
    strength_range: { min: 200, max: 600 },
    
    medical: {
      medics_per_company: 2,
      aid_station_staff: 4,
      ambulances: 2,
      
      expected_daily_casualties: {
        low: 0,
        moderate: 1,
        high: 4,
        extreme: 10
      }
    },
    
    modern_warfare: {
      drone_strike_risk: 'MODERATE',
      artillery_exposure: 'LOW',
      electronic_signature: 'EXTREME',
      thermal_signature: 'MODERATE'
    },
    
    description: 'Signal/communications - EW targets',
    educational_note: 'Electronic emissions attract targeting'
  },

  // 2026-10-24 (naming audit step 4): HQ was a brigade type only; the HQ card of a brigade (brigadeCards.js) and the HQ elements of the
  // older builds are battalion-level cards, and a type missing from this table fell back to the MECHANIZED configuration (it also
  // got a Role 1 by the needs_role1 default). Casualty profile = UNIT_TYPES.HQ. Strength: the headquarters company of the US brigade
  // combat teams, 174 to 185 (SM 3-90, docs/research/RESEARCH_BRIGADE_SIZES.md); position: the brigade headquarters (EDUCATED GUESS 15 km).
  HQ: {
    value: 'HQ',
    label: 'Headquarters',
    icon: '⭐',
    category: 'HQ',

    casualty_modifier: 0.35,

    ukraine_data: {
      base_rate: 0.5,
      drone_vulnerability: 2.0,      // command posts are priority drone targets
      artillery_vulnerability: 1.5,
      wia_kia_ratio: 3.0,
      casualty_notes: 'Command posts suffer targeting strikes - HIMARS, missiles, drones'
    },

    has_frontage: false,
    needs_role1: false,             // the US BCT headquarters has a surgeon section, no treatment team (RESEARCH_BRIGADE_ROLE1.md)
    typical_position_km: 15,

    operational_state: 'SUPPORT_ACTIVE',
    default_posture: 'DELIBERATE_DEFENSE',
    default_mission: 'COMMAND',

    typical_strength: 180,
    strength_range: { min: 100, max: 300 },

    description: 'Headquarters and headquarters company - command and control element',
    educational_note: 'Command posts are high-value targets'
  },

  // -------------------------------------------------------------------------
  // 2026-10-24 (Dr. Tatar: define the six real functions of the listed types; research: docs/research/RESEARCH_LISTED_UNIT_TYPES.md).
  // Every number below is an EDUCATED GUESS anchored on an already defined type; the public record has no per-person casualty rate
  // and no unit table for these (sizes: Ranger regiment 3,566, SF group 1,200 to 1,500, attack battalion about 400 are SECONDARY).
  // -------------------------------------------------------------------------
  SPECIAL_FORCES: {
    value: 'SPECIAL_FORCES',
    label: 'Special Forces Battalion',
    icon: '🎯',
    category: 'COMBAT',
    
    // Casualty modifiers
    casualty_modifier: 0.5,   // EDUCATED GUESS, Dr. Tatar 2026-10-24 (0.7 first, lowered to 0.5 the same day after the bracket; expert judgment, no public rate; the public totals bracket it at 0.34 to 0.7, RESEARCH_LISTED_UNIT_TYPES.md 9.2): medium: small specialised teams, dangerous work, but unseen and rarely in the line of battle; not the infantry 1.2
    
    // Ukraine-validated casualty data
    ukraine_data: {
      base_rate: 0.8,               // medium (Dr. Tatar 2026-10-24); the infantry value 1.25 was the starting copy
      artillery_vulnerability: 1.4,  // Very vulnerable to artillery
      drone_vulnerability: 1.2,      // Moderately vulnerable to drones
      mine_vulnerability: 1.5,       // High mine casualties
      
      wia_kia_ratio: 3.5,           // Higher survival due to lighter wounds
      casualty_notes: 'Small specialised teams: dangerous work, losses seldom reported or recognised until something big happens'
    },
    
    // Position and frontage
    has_frontage: true,
    needs_role1: true,
    typical_position_km: 5,
    frontage_km: { min: 2, max: 5 },
    
    // Operational defaults
    operational_state: 'COMBAT_ENGAGED',
    default_posture: 'HASTY_DEFENSE',
    default_mission: 'DEFENSE',
    
    // Strength
    typical_strength: 350,   // DERIVED: a special forces group is about 1,400 (CRS RS21048) with four battalions; the Ranger battalion is about 800 (alias RANGER)
    strength_range: { min: 150, max: 1400 },
    
    // Medical requirements (enhanced)
    medical: {
      medics_per_company: 6,
      aid_station_staff: 8,
      ambulances: 4,
      enhanced_ifak: true,
      
      expected_daily_casualties: {
        low: 2,
        moderate: 8,
        high: 25,
        extreme: 50
      }
    },
    
    // Modern warfare modifiers
    modern_warfare: {
      drone_strike_risk: 'HIGH',
      artillery_exposure: 'VERY_HIGH',
      electronic_signature: 'LOW',
      thermal_signature: 'MODERATE'
    },
    
    description: 'Special forces: small specialised teams (a detachment is 12), a group is 1,200 to 1,500; the Ranger line battalion (about 800) is an alias',
    educational_note: 'Small teams working unseen: medium casualties, high consequence'
  },

  ATTACK_AVIATION: {
    value: 'ATTACK_AVIATION',
    label: 'Attack Aviation Battalion',
    icon: '🚁',
    category: 'COMBAT',
    
    casualty_modifier: 0.2,   // EDUCATED GUESS, Dr. Tatar 2026-10-24 (expert judgment, no public rate): employed sparingly while drones and enemy air defense are high, so low personnel exposure; aircraft losses are not personnel
    
    ukraine_data: {
      base_rate: 0.5,
      air_defense_vulnerability: 2.5, // MANPADS devastating
      drone_vulnerability: 0.8,       // Air assets harder to target
      
      wia_kia_ratio: 1.5,            // Aircraft crashes often fatal
      aircraft_loss_rate: 0.05,      // Per mission high-risk
      casualty_notes: 'Very high per-incident lethality'
    },
    
    has_frontage: false,
    needs_role1: true,
    typical_position_km: 25,
    
    operational_state: 'SUPPORT_ACTIVE',
    default_posture: 'RESERVE',
    default_mission: 'SUPPORT',
    
    typical_strength: 400,   // EDUCATED GUESS, see docs/research/RESEARCH_LISTED_UNIT_TYPES.md
    strength_range: { min: 300, max: 500 },
    
    medical: {
      medics_per_company: 2,
      flight_surgeons: 2,
      medevac_aircraft: 8,
      sar_capability: true,
      
      expected_daily_casualties: {
        low: 0,
        moderate: 1,
        high: 4,
        extreme: 12
      }
    },
    
    modern_warfare: {
      drone_strike_risk: 'LOW',       // Hard to target in air
      artillery_exposure: 'MODERATE', // Airfields targeted
      electronic_signature: 'HIGH',
      thermal_signature: 'VERY_HIGH',
      
      manpads_threat: 'EXTREME'
    },
    
    description: 'Attack helicopter battalion (24 aircraft, about 400 people); strikes by sortie from airfields and forward arming points',

    // -------------------------------------------------------------------------
    // DOCTRINAL COMPOSITION (v2.10 - UT-GAP-003)
    // Combat Aviation Brigade — attack, assault, general support aviation
    // Source: FM 3-04 Army Aviation; US CAB organization; NATO aviation brigade
    // -------------------------------------------------------------------------
    doctrinal_composition: {
      source: 'US Combat Aviation Brigade Organization (generic)',
      reference: 'FM 3-04; ATP 3-04.1; US CAB MTOE 2018',

      brigade: {
        total_strength: 3000,
        sub_units: [
          {
            type: 'BATTALION',
            unit_type: 'AVIATION',
            count: 2,
            strength_each: 700,
            name_pattern: '{parent} Attack/Assault Battalion {index}',
            notes: 'Attack-assault battalions — AH-64 Apache and UH-60 mix'
          },
          {
            type: 'BATTALION',
            unit_type: 'AVIATION',
            count: 1,
            strength_each: 600,
            name_pattern: '{parent} General Support Aviation Battalion',
            notes: 'General support — CH-47 Chinook heavy lift and MEDEVAC'
          },
          {
            type: 'BATTALION',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 500,
            name_pattern: '{parent} Aviation Support Battalion',
            notes: 'ASB — aircraft maintenance, FARP ops, supply'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 200,
            name_pattern: '{parent} Headquarters Company',
            notes: 'CAB HQ — airspace C2, liaison'
          }
        ]
      },

      battalion: {
        total_strength: 700,
        sub_units: [
          {
            type: 'COMPANY',
            unit_type: 'AVIATION',
            count: 3,
            strength_each: 150,
            name_pattern: '{parent} Company {letter}',
            notes: 'Aviation companies — 8 aircraft each (varies by type)'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Battalion HQ and maintenance platoon'
          },
          {
            type: 'COMPANY',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Forward Support Company',
            notes: 'Aviation FSC — forward maintenance and supply'
          }
        ]
      }
    },

    educational_note: 'Aircraft losses are high (Karbala 2003, Ka-52 in Ukraine) while personnel casualties are few'
  },

  MILITARY_POLICE: {
    value: 'MILITARY_POLICE',
    label: 'Military Police Battalion',
    icon: '🛡️',
    category: 'SUPPORT',
    
    casualty_modifier: 0.2,   // EDUCATED GUESS, Dr. Tatar 2026-10-24 (expert judgment, no public rate): usually not fighting: rear-area security, traffic, prisoners
    
    ukraine_data: {
      base_rate: 0.35,
      artillery_vulnerability: 0.6,
      drone_vulnerability: 1.4,      // Convoys visible
      convoy_ambush: 1.3,
      
      wia_kia_ratio: 4.0,
      vehicle_loss_rate: 0.10,
      casualty_notes: 'Convoy operations risky; depot attacks'
    },
    
    has_frontage: false,
    needs_role1: true,
    typical_position_km: 30,
    
    operational_state: 'SUPPORT_ACTIVE',
    default_posture: 'RESERVE',
    default_mission: 'SUPPORT',
    
    typical_strength: 500,   // EDUCATED GUESS near the 1964 army-level MP battalion (TOE 19-35F: 595, company 180); no current table is public
    strength_range: { min: 300, max: 900 },
    
    medical: {
      medics_per_company: 2,
      aid_station_staff: 4,
      ambulances: 2,
      
      expected_daily_casualties: {
        low: 0,
        moderate: 1,
        high: 4,
        extreme: 12
      }
    },
    
    modern_warfare: {
      drone_strike_risk: 'HIGH',      // Logistics visible
      artillery_exposure: 'LOW',
      electronic_signature: 'LOW',
      thermal_signature: 'MODERATE'
    },
    
    description: 'Rear-area security, main supply routes, traffic control, prisoners',
    educational_note: 'WWII military police were 0.14 percent of US Army battle casualties'
  },

  CBRN_DEFENSE: {
    value: 'CBRN_DEFENSE',
    label: 'CBRN Defence Battalion',
    icon: '☢️',
    category: 'SUPPORT',
    
    casualty_modifier: 0.2,   // EDUCATED GUESS, Dr. Tatar 2026-10-24 (expert judgment, no public rate): protection, not a first-line fighting unit
    
    ukraine_data: {
      base_rate: 0.45,
      artillery_vulnerability: 0.8,
      drone_vulnerability: 1.2,
      electronic_attack: 1.5,        // EW targeting
      
      wia_kia_ratio: 3.5,
      casualty_notes: 'EW targeting based on emissions'
    },
    
    has_frontage: false,
    needs_role1: false,
    typical_position_km: 20,
    
    operational_state: 'SUPPORT_ACTIVE',
    default_posture: 'DELIBERATE_DEFENSE',
    default_mission: 'SUPPORT',
    
    typical_strength: 300,   // EDUCATED GUESS, see docs/research/RESEARCH_LISTED_UNIT_TYPES.md
    strength_range: { min: 150, max: 500 },
    
    medical: {
      medics_per_company: 2,
      aid_station_staff: 4,
      ambulances: 2,
      
      expected_daily_casualties: {
        low: 0,
        moderate: 1,
        high: 4,
        extreme: 10
      }
    },
    
    modern_warfare: {
      drone_strike_risk: 'MODERATE',
      artillery_exposure: 'LOW',
      electronic_signature: 'EXTREME',
      thermal_signature: 'MODERATE'
    },
    
    description: 'CBRN reconnaissance and decontamination (Romanian: apărare CBRN; US: Chemical Corps); battalions at division level, companies in brigades',
    educational_note: 'WWII US Chemical Corps was 0.4 percent of Army battle casualties'
  },

  CIVIL_AFFAIRS: {
    value: 'CIVIL_AFFAIRS',
    label: 'Civil Affairs / CIMIC Battalion',
    icon: '🤝',
    category: 'SUPPORT',
    
    casualty_modifier: 0.15,   // EDUCATED GUESS, Dr. Tatar 2026-10-24 (expert judgment, no public rate): civil-military relations, not usually fighting
    
    ukraine_data: {
      base_rate: 0.35,
      artillery_vulnerability: 0.6,
      drone_vulnerability: 1.4,      // Convoys visible
      convoy_ambush: 1.3,
      
      wia_kia_ratio: 4.0,
      vehicle_loss_rate: 0.10,
      casualty_notes: 'Convoy operations risky; depot attacks'
    },
    
    has_frontage: false,
    needs_role1: false,
    typical_position_km: 30,
    
    operational_state: 'SUPPORT_ACTIVE',
    default_posture: 'RESERVE',
    default_mission: 'SUPPORT',
    
    typical_strength: 200,   // EDUCATED GUESS, see docs/research/RESEARCH_LISTED_UNIT_TYPES.md
    strength_range: { min: 100, max: 400 },
    
    medical: {
      medics_per_company: 2,
      aid_station_staff: 4,
      ambulances: 2,
      
      expected_daily_casualties: {
        low: 0,
        moderate: 1,
        high: 4,
        extreme: 12
      }
    },
    
    modern_warfare: {
      drone_strike_risk: 'HIGH',      // Logistics visible
      artillery_exposure: 'LOW',
      electronic_signature: 'LOW',
      thermal_signature: 'MODERATE'
    },
    
    description: 'Civil-military cooperation: teams with maneuver units and in the rear area (Romanian 1st CIMIC Battalion)',
    educational_note: 'US special operations civil affairs soldiers were 33 of 377 fallen since 2001; no unit table is public'
  },

  PSYCHOLOGICAL_OPS: {
    value: 'PSYCHOLOGICAL_OPS',
    label: 'Psychological Operations Battalion',
    icon: '📢',
    category: 'SUPPORT',
    
    casualty_modifier: 0.15,   // EDUCATED GUESS, Dr. Tatar 2026-10-24 (expert judgment, no public rate): not seen in the first line
    
    ukraine_data: {
      base_rate: 0.35,
      artillery_vulnerability: 0.6,
      drone_vulnerability: 1.4,      // Convoys visible
      convoy_ambush: 1.3,
      
      wia_kia_ratio: 4.0,
      vehicle_loss_rate: 0.10,
      casualty_notes: 'Convoy operations risky; depot attacks'
    },
    
    has_frontage: false,
    needs_role1: false,
    typical_position_km: 30,
    
    operational_state: 'SUPPORT_ACTIVE',
    default_posture: 'RESERVE',
    default_mission: 'SUPPORT',
    
    typical_strength: 200,   // EDUCATED GUESS, see docs/research/RESEARCH_LISTED_UNIT_TYPES.md
    strength_range: { min: 100, max: 400 },
    
    medical: {
      medics_per_company: 2,
      aid_station_staff: 4,
      ambulances: 2,
      
      expected_daily_casualties: {
        low: 0,
        moderate: 1,
        high: 4,
        extreme: 12
      }
    },
    
    modern_warfare: {
      drone_strike_risk: 'HIGH',      // Logistics visible
      artillery_exposure: 'LOW',
      electronic_signature: 'LOW',
      thermal_signature: 'MODERATE'
    },
    
    description: 'Psychological operations teams with maneuver units and in the rear area',
    educational_note: 'US psychological operations soldiers were 15 of 377 fallen since 2001; no unit table is public'
  },

  
  // -------------------------------------------------------------------------
  // COMBAT SERVICE SUPPORT TYPES (Low engagement, low casualties)
  // -------------------------------------------------------------------------
  LOGISTICS: {
    value: 'LOGISTICS',
    label: 'Logistics Battalion',
    icon: '📦',
    category: 'CSS',
    
    casualty_modifier: 0.2,   // 2026-10-11: was 0.3 (rear exposure scaled by about 0.7, Dr. Tatar; EDUCATED GUESS)
    
    ukraine_data: {
      base_rate: 0.35,
      artillery_vulnerability: 0.6,
      drone_vulnerability: 1.4,      // Convoys visible
      convoy_ambush: 1.3,
      
      wia_kia_ratio: 4.0,
      vehicle_loss_rate: 0.10,
      casualty_notes: 'Convoy operations risky; depot attacks'
    },
    
    has_frontage: false,
    needs_role1: true,               // v2.2 FB-003: LOGISTICS soldiers need medical care
    typical_position_km: 40,        // Rear area
    
    operational_state: 'TRUE_RESERVE',
    default_posture: 'RESERVE',
    default_mission: 'SUPPORT',
    
    typical_strength: 500,
    strength_range: { min: 300, max: 700 },
    
    medical: {
      medics_per_company: 2,
      aid_station_staff: 4,
      ambulances: 2,
      
      expected_daily_casualties: {
        low: 0,
        moderate: 1,
        high: 4,
        extreme: 12
      }
    },
    
    modern_warfare: {
      drone_strike_risk: 'HIGH',      // Logistics visible
      artillery_exposure: 'LOW',
      electronic_signature: 'LOW',
      thermal_signature: 'MODERATE'
    },
    
    description: 'Supply/transport - convoy risk',
    educational_note: 'Deep strike and drone attacks on logistics increasing'
  },
  
  MAINTENANCE: {
    value: 'MAINTENANCE',
    label: 'Maintenance Battalion',
    icon: '🔩',
    category: 'CSS',
    
    casualty_modifier: 0.18,   // 2026-10-11: was 0.25 (rear exposure scaled by about 0.7, Dr. Tatar; EDUCATED GUESS)
    
    ukraine_data: {
      base_rate: 0.3,
      artillery_vulnerability: 0.5,
      drone_vulnerability: 1.2,
      
      wia_kia_ratio: 4.5,
      casualty_notes: 'Rear area, occasional deep strikes'
    },
    
    has_frontage: false,
    needs_role1: false,             // v2.4.0: Pure support, no organic Role 1
    typical_position_km: 50,        // Deep rear area
    
    operational_state: 'TRUE_RESERVE',
    default_posture: 'RESERVE',
    default_mission: 'SUPPORT',
    
    typical_strength: 400,
    strength_range: { min: 200, max: 600 },
    
    medical: {
      medics_per_company: 2,
      aid_station_staff: 3,
      ambulances: 1,
      
      expected_daily_casualties: {
        low: 0,
        moderate: 0,
        high: 2,
        extreme: 8
      }
    },
    
    modern_warfare: {
      drone_strike_risk: 'MODERATE',
      artillery_exposure: 'VERY_LOW',
      electronic_signature: 'LOW',
      thermal_signature: 'LOW'
    },
    
    description: 'Equipment maintenance - deep rear',
    educational_note: 'Relatively safe but still subject to deep strikes'
  },
  
  MEDICAL: {
    value: 'MEDICAL',
    label: 'Medical Battalion',
    icon: '🏥',
    category: 'CSS',
    
    casualty_modifier: 0.15,   // 2026-10-11: was 0.2 (rear exposure scaled by about 0.7, Dr. Tatar; EDUCATED GUESS)
    
    ukraine_data: {
      base_rate: 0.25,
      artillery_vulnerability: 0.4,   // Should be protected
      drone_vulnerability: 1.2,       // Still targeted despite markings
      deliberate_targeting: 1.3,      // Medical marked vehicles targeted
      
      wia_kia_ratio: 4.0,
      casualty_notes: 'Medical markings provide limited protection in modern war'
    },
    
    has_frontage: false,
    needs_role1: false,             // v2.4.0: Medical units ARE the providers
    typical_position_km: 30,        // Rear area
    
    operational_state: 'TRUE_RESERVE',
    default_posture: 'RESERVE',
    default_mission: 'SUPPORT',
    
    typical_strength: 300,
    strength_range: { min: 150, max: 500 },
    
    is_medical_provider: true,
    
    medical: {
      // Medical units are the providers, not consumers
      provides_role: 'ROLE_2',
      organic_ambulances: 20,
      surgical_teams: 2
    },
    
    modern_warfare: {
      drone_strike_risk: 'MODERATE',
      artillery_exposure: 'LOW',
      electronic_signature: 'LOW',
      thermal_signature: 'LOW',
      
      protection_note: 'Geneva Convention markings provide LIMITED protection'
    },
    
    description: 'Organic medical treatment element — Role 2 provider (medical company/battalion abstracted). Present in all NATO armies: Romanian Compania Medicală, German Sanitätskompanie, French Compagnie Médicale, US Medical Company. TREATS casualties — does NOT consume Role 1.',
    educational_note: 'Valid for all nations. Ukraine showed medical vehicles targeted despite Geneva Convention markings; medical personnel casualties significant. Do NOT confuse with Medical Support Battalion (logistics only).'
  },
  
  // ============================================================================
  // v2.2 CSS-003: New Battalion Types for CSS Brigades
  // ============================================================================
  
  TRANSPORT: {
    value: 'TRANSPORT',
    label: 'Transport Battalion',
    icon: '🚛',
    category: 'CSS',
    
    casualty_modifier: 0.2,   // 2026-10-11: was 0.3 (rear exposure scaled by about 0.7, Dr. Tatar; EDUCATED GUESS)
    
    ukraine_data: {
      base_rate: 0.35,
      artillery_vulnerability: 0.5,
      drone_vulnerability: 1.5,       // Convoys highly visible
      convoy_ambush: 1.4,             // Main Roads vulnerable
      
      wia_kia_ratio: 4.0,
      vehicle_loss_rate: 0.12,
      casualty_notes: 'Convoy operations high-risk; main supply routes targeted'
    },
    
    has_frontage: false,
    needs_role1: true,               // v2.2: 500+ personnel need medical care
    typical_position_km: 50,         // Deep rear - supply routes
    
    operational_state: 'TRUE_RESERVE',
    default_posture: 'RESERVE',
    default_mission: 'SUPPORT',
    
    typical_strength: 500,
    strength_range: { min: 350, max: 650 },
    
    medical: {
      medics_per_company: 2,
      aid_station_staff: 4,
      ambulances: 2,
      
      expected_daily_casualties: {
        low: 0,
        moderate: 2,
        high: 6,
        extreme: 15
      }
    },
    
    modern_warfare: {
      drone_strike_risk: 'HIGH',      // Convoys visible from air
      artillery_exposure: 'MODERATE', // Main roads targeted
      electronic_signature: 'LOW',
      thermal_signature: 'HIGH'       // Engine heat signatures
    },
    
    description: 'Transport/movement control - convoy operations',
    educational_note: 'Ukraine: 40% of logistics losses from drone strikes on convoys'
  },
  
  SUPPLY: {
    value: 'SUPPLY',
    label: 'Supply Battalion',
    icon: '📦',
    category: 'CSS',
    
    casualty_modifier: 0.18,   // 2026-10-11: was 0.25 (rear exposure scaled by about 0.7, Dr. Tatar; EDUCATED GUESS)
    
    ukraine_data: {
      base_rate: 0.30,
      artillery_vulnerability: 0.6,   // Depots can be targeted
      drone_vulnerability: 1.3,
      
      wia_kia_ratio: 4.5,
      vehicle_loss_rate: 0.08,
      casualty_notes: 'Depot operations; ammunition handling accidents'
    },
    
    has_frontage: false,
    needs_role1: true,               // v2.2: 400+ personnel need medical care
    typical_position_km: 60,         // Deep rear - supply points
    
    operational_state: 'TRUE_RESERVE',
    default_posture: 'RESERVE',
    default_mission: 'SUPPORT',
    
    typical_strength: 400,
    strength_range: { min: 250, max: 550 },
    
    medical: {
      medics_per_company: 2,
      aid_station_staff: 3,
      ambulances: 1,
      
      expected_daily_casualties: {
        low: 0,
        moderate: 1,
        high: 4,
        extreme: 10
      }
    },
    
    modern_warfare: {
      drone_strike_risk: 'MODERATE',
      artillery_exposure: 'LOW',
      electronic_signature: 'LOW',
      thermal_signature: 'MODERATE'
    },
    
    description: 'Supply point operations - Class I-IX distribution',
    educational_note: 'Ammunition handling requires safety protocols; depot defense important'
  },
  
  MEDICAL_SUPPORT: {
    value: 'MEDICAL_SUPPORT',
    label: 'Medical Support Battalion',
    icon: '🩺',
    category: 'CSS',
    
    casualty_modifier: 0.15,   // 2026-10-11: was 0.2 (rear exposure scaled by about 0.7, Dr. Tatar; EDUCATED GUESS)
    
    ukraine_data: {
      base_rate: 0.25,
      artillery_vulnerability: 0.4,
      drone_vulnerability: 1.2,
      
      wia_kia_ratio: 4.0,
      casualty_notes: 'Medical logistics - blood products, medical materiel distribution'
    },
    
    has_frontage: false,
    needs_role1: false,              // Personnel treated in own facilities
    typical_position_km: 40,         // Near Role 2/3 facilities
    
    operational_state: 'TRUE_RESERVE',
    default_posture: 'RESERVE',
    default_mission: 'SUPPORT',
    
    typical_strength: 350,
    strength_range: { min: 200, max: 450 },
    
    is_medical_provider: false,      // Logistics, not treatment
    provides_medical_logistics: true,
    
    medical: {
      // Medical logistics - supplies, not treatment
      blood_storage_capacity: 500,   // Units of blood products
      class_viii_throughput: 10000,  // kg/day
      ambulance_maintenance: true
    },
    
    modern_warfare: {
      drone_strike_risk: 'MODERATE',
      artillery_exposure: 'LOW',
      electronic_signature: 'LOW',
      thermal_signature: 'LOW'
    },
    
    description: 'Medical LOGISTICS unit — Medical materiel (AJP-4.10C) supply, blood products storage/distribution, ambulance maintenance. NO treatment capability. Feeds Role 2/3 facilities with consumables. Equivalent to: US Medical Logistics Company (Class VIII), NATO BSB medical supply element, JLSG medical supply node.',
    educational_note: 'Select at division/corps level for dedicated medical logistics. Do NOT use at brigade level instead of Medical Battalion — this unit does not treat casualties. Critical for blood supply chain and walking blood bank operations.'
  },
  
  // -------------------------------------------------------------------------
  // v2.12: AIRBORNE, AIR_ASSAULT, MOUNTAIN — previously missing from BATTALION_TYPES.
  // Brigade-level UNIT_TYPES entries existed but battalion fallback was MECHANIZED
  // (wrong modifier and position). Values confirmed March 24, 2026.
  // -------------------------------------------------------------------------

  AIRBORNE: {
    value: 'AIRBORNE',
    label: 'Airborne Infantry Battalion',
    icon: '🪂',
    category: 'COMBAT',

    casualty_modifier: 1.3,

    ukraine_data: {
      base_rate: 1.3,
      drone_vulnerability: 1.3,      // Lightly equipped, limited AD
      artillery_vulnerability: 1.2,  // No armour protection
      mine_vulnerability: 1.1,

      wia_kia_ratio: 2.8,
      casualty_notes: 'Light infantry, no armour — higher casualties per engagement'
    },

    has_frontage: true,
    needs_role1: true,
    typical_position_km: 2,
    frontage_km: { min: 3, max: 10 },

    operational_state: 'COMBAT_ENGAGED',
    default_posture: 'HASTY_DEFENSE',
    default_mission: 'MAIN_EFFORT',

    typical_strength: 600,
    strength_range: { min: 500, max: 700 },

    medical: {
      medics_per_company: 4,
      aid_station_staff: 8,
      ambulances: 3,
      forward_aid_capability: true,

      expected_daily_casualties: {
        low: 2,
        moderate: 8,
        high: 25,
        extreme: 60
      }
    },

    description: 'Parachute or air-landed infantry — light, fast, no heavy armour protection',
    educational_note: 'Higher casualties than mechanized due to lack of armour; critical TCCC requirement'
  },

  AIR_ASSAULT: {
    value: 'AIR_ASSAULT',
    label: 'Air Assault Battalion',
    icon: '🚁',
    category: 'COMBAT',

    casualty_modifier: 1.2,

    ukraine_data: {
      base_rate: 1.2,
      drone_vulnerability: 1.4,      // Helicopters extremely vulnerable to drones
      artillery_vulnerability: 1.1,
      mine_vulnerability: 1.0,

      wia_kia_ratio: 2.9,
      casualty_notes: 'Helicopter insertion exposes force during approach and landing'
    },

    has_frontage: true,
    needs_role1: true,
    typical_position_km: 2,
    frontage_km: { min: 3, max: 10 },

    operational_state: 'COMBAT_ENGAGED',
    default_posture: 'HASTY_DEFENSE',
    default_mission: 'MAIN_EFFORT',

    typical_strength: 650,
    strength_range: { min: 550, max: 750 },

    medical: {
      medics_per_company: 4,
      aid_station_staff: 8,
      ambulances: 2,
      medevac_aircraft: 2,
      forward_aid_capability: true,

      expected_daily_casualties: {
        low: 2,
        moderate: 7,
        high: 22,
        extreme: 55
      }
    },

    description: 'Helicopter-inserted infantry — fast insertion, lightly equipped, high drone exposure',
    educational_note: 'Ukraine data shows helicopter operations extremely high-risk; MEDEVAC integration critical'
  },

  MOUNTAIN: {
    value: 'MOUNTAIN',
    label: 'Mountain Infantry Battalion',
    icon: '⛰️',
    category: 'COMBAT',

    casualty_modifier: 1.15,

    ukraine_data: {
      base_rate: 1.1,
      drone_vulnerability: 1.1,
      artillery_vulnerability: 1.1,
      mine_vulnerability: 1.2,       // Mountain terrain — mine/IED exposure

      wia_kia_ratio: 3.0,
      casualty_notes: 'Terrain constraints slow evacuation; environmental casualties elevated'
    },

    has_frontage: true,
    needs_role1: true,
    typical_position_km: 3,
    frontage_km: { min: 5, max: 15 },

    operational_state: 'COMBAT_ENGAGED',
    default_posture: 'DELIBERATE_DEFENSE',
    default_mission: 'MAIN_EFFORT',

    typical_strength: 700,
    strength_range: { min: 600, max: 800 },

    medical: {
      medics_per_company: 5,
      aid_station_staff: 8,
      ambulances: 2,
      forward_aid_capability: true,

      expected_daily_casualties: {
        low: 1,
        moderate: 6,
        high: 18,
        extreme: 45
      }
    },

    description: 'Mountain warfare infantry — terrain specialist, high environmental DNBI',
    educational_note: 'Altitude and cold increase DNBI rates; evacuation extremely difficult in mountain terrain'
  },

  AVIATION: {
    value: 'AVIATION',
    label: 'Aviation Battalion',
    icon: '🚁',
    category: 'COMBAT',
    
    casualty_modifier: 0.3,   // 2026-10-11: was 0.4 (rear exposure scaled by about 0.7, Dr. Tatar; EDUCATED GUESS)
    
    ukraine_data: {
      base_rate: 0.5,
      air_defense_vulnerability: 2.5, // MANPADS devastating
      drone_vulnerability: 0.8,       // Air assets harder to target
      
      wia_kia_ratio: 1.5,            // Aircraft crashes often fatal
      aircraft_loss_rate: 0.05,      // Per mission high-risk
      casualty_notes: 'Very high per-incident lethality'
    },
    
    has_frontage: false,
    needs_role1: true,              // BUG-177 FIX: combat types need organic Role 1
    typical_position_km: 25,   // v2.8 FIX: was -25 (sign error) — aviation battalions operate from airfields behind FEBA
    
    operational_state: 'SUPPORT_ACTIVE',
    default_posture: 'RESERVE',
    default_mission: 'SUPPORT',
    
    typical_strength: 400,
    strength_range: { min: 200, max: 600 },
    
    medical: {
      medics_per_company: 2,
      flight_surgeons: 2,
      medevac_aircraft: 8,
      sar_capability: true,
      
      expected_daily_casualties: {
        low: 0,
        moderate: 1,
        high: 4,
        extreme: 12
      }
    },
    
    modern_warfare: {
      drone_strike_risk: 'LOW',       // Hard to target in air
      artillery_exposure: 'MODERATE', // Airfields targeted
      electronic_signature: 'HIGH',
      thermal_signature: 'VERY_HIGH',
      
      manpads_threat: 'EXTREME'
    },
    
    description: 'Rotary-wing aviation - MANPADS threat',

    // -------------------------------------------------------------------------
    // DOCTRINAL COMPOSITION (v2.10 - UT-GAP-003)
    // Combat Aviation Brigade — attack, assault, general support aviation
    // Source: FM 3-04 Army Aviation; US CAB organization; NATO aviation brigade
    // -------------------------------------------------------------------------
    doctrinal_composition: {
      source: 'US Combat Aviation Brigade Organization (generic)',
      reference: 'FM 3-04; ATP 3-04.1; US CAB MTOE 2018',

      brigade: {
        total_strength: 3000,
        sub_units: [
          {
            type: 'BATTALION',
            unit_type: 'AVIATION',
            count: 2,
            strength_each: 700,
            name_pattern: '{parent} Attack/Assault Battalion {index}',
            notes: 'Attack-assault battalions — AH-64 Apache and UH-60 mix'
          },
          {
            type: 'BATTALION',
            unit_type: 'AVIATION',
            count: 1,
            strength_each: 600,
            name_pattern: '{parent} General Support Aviation Battalion',
            notes: 'General support — CH-47 Chinook heavy lift and MEDEVAC'
          },
          {
            type: 'BATTALION',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 500,
            name_pattern: '{parent} Aviation Support Battalion',
            notes: 'ASB — aircraft maintenance, FARP ops, supply'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 200,
            name_pattern: '{parent} Headquarters Company',
            notes: 'CAB HQ — airspace C2, liaison'
          }
        ]
      },

      battalion: {
        total_strength: 700,
        sub_units: [
          {
            type: 'COMPANY',
            unit_type: 'AVIATION',
            count: 3,
            strength_each: 150,
            name_pattern: '{parent} Company {letter}',
            notes: 'Aviation companies — 8 aircraft each (varies by type)'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Battalion HQ and maintenance platoon'
          },
          {
            type: 'COMPANY',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Forward Support Company',
            notes: 'Aviation FSC — forward maintenance and supply'
          }
        ]
      }
    },

    educational_note: 'Very high lethality per incident; contested airspace limits operations'
  }
};

// ============================================================================
// BRIGADE-LEVEL UNIT TYPES
// ============================================================================

export const UNIT_TYPES = {
  // -------------------------------------------------------------------------
  // COMBAT BRIGADE TYPES
  // -------------------------------------------------------------------------
  HEAVY: {
    value: 'HEAVY',
    label: 'Armored',
    icon: '🛡️',
    color: '#4caf50',
    category: 'COMBAT',
    
    casualty_modifier: 1.0,
    
    ukraine_data: {
      base_rate: 1.0,
      drone_vulnerability: 1.4,
      artillery_vulnerability: 1.1,
      
      wia_kia_ratio: 2.5,
      vehicle_focus: true,
      casualty_notes: 'High vehicle losses, lower personnel casualties'
    },
    
    has_frontage: true,
    typical_position_km: 5,
    frontage_km: { min: 15, max: 30 },
    
    default_posture: 'DELIBERATE_DEFENSE',
    default_mission: 'DEFENSE',
    operational_state: 'COMBAT_ENGAGED',
    
    typical_strength: 5000,
    
    battalion_composition: {
      ARMOR: 2,
      MECHANIZED: 1,
      ARTILLERY: 1,
      ENGINEERS: 1
    },

    // -------------------------------------------------------------------------
    // DOCTRINAL COMPOSITION (v2.9 - BUG FIX)
    // NATO/generic heavy armored brigade — 3 combined-arms battalions
    // Mirrors ARMOR doctrinal_composition (HEAVY is the generic key;
    // ARMOR is the US-specific alias with identical structure).
    // Source: FM 3-96 BCT Organization (2016-2023); NATO heavy brigade doctrine;
    //   Romanian 1st Armoured Brigade; German Panzerbrigade
    // -------------------------------------------------------------------------
    doctrinal_composition: {
      source: 'NATO Heavy Brigade Organization (generic)',
      reference: 'FM 3-96 BCT Organization; ATP-3.2.1; NATO heavy brigade doctrine',

      brigade: {
        total_strength: 5000,
        sub_units: [
          {
            type: 'BATTALION',
            unit_type: 'ARMOR',
            count: 2,
            strength_each: 850,
            name_pattern: '{parent} Armoured Battalion {index}',
            notes: 'Tank-heavy combined arms battalions — primary shock element'
          },
          {
            type: 'BATTALION',
            unit_type: 'MECHANIZED',
            count: 1,
            strength_each: 800,
            name_pattern: '{parent} Mechanized Battalion',
            notes: 'Mechanized infantry battalion — close protection for armour'
          },
          {
            type: 'BATTALION',
            unit_type: 'ARTILLERY',
            count: 1,
            strength_each: 500,
            name_pattern: '{parent} Artillery Battalion',
            notes: 'Direct support artillery (155mm self-propelled)'
          },
          {
            type: 'BATTALION',
            unit_type: 'ENGINEERS',
            count: 1,
            strength_each: 600,
            name_pattern: '{parent} Engineer Battalion',
            notes: 'Combat engineers — armoured breaching and obstacle crossing'
          },
          {
            type: 'BATTALION',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 750,
            name_pattern: '{parent} Support Battalion',
            notes: 'Heavy logistics — high fuel and ammunition consumption'
          },
          {
            type: 'SQUADRON',
            unit_type: 'RECONNAISSANCE',
            count: 1,
            strength_each: 400,
            name_pattern: '{parent} Reconnaissance Squadron',
            notes: 'Armoured reconnaissance — screening and intelligence'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Brigade HQ — command and control'
          }
        ]
      },

      battalion: {
        total_strength: 850,
        sub_units: [
          {
            type: 'COMPANY',
            unit_type: 'ARMOR',
            count: 2,
            strength_each: 160,
            name_pattern: '{parent} Company {letter}',
            notes: 'Tank companies (14 MBT each)'
          },
          {
            type: 'COMPANY',
            unit_type: 'MECHANIZED',
            count: 2,
            strength_each: 150,
            name_pattern: '{parent} Company {letter}',
            notes: 'Mechanized infantry companies — tank escort'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Battalion HQ'
          },
          {
            type: 'COMPANY',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 180,
            name_pattern: '{parent} Forward Support Company',
            notes: 'High maintenance demand — tank-heavy unit'
          }
        ]
      }
    },

    medical: {
      role_2_basic: 1,
      role_1_stations: 5,
      ambulances: 16,
      medevac_organic: false
    },
    
    description: 'Tank and mechanized units with heavy firepower'
  },
  
  INFANTRY: {
    value: 'INFANTRY',
    label: 'Infantry',
    icon: '🎖️',
    color: '#4caf50',
    category: 'COMBAT',
    
    casualty_modifier: 1.2,
    
    ukraine_data: {
      base_rate: 1.25,
      drone_vulnerability: 1.2,
      artillery_vulnerability: 1.4,
      
      wia_kia_ratio: 3.5,
      casualty_notes: 'Highest absolute casualties'
    },
    
    has_frontage: true,
    typical_position_km: 3,
    frontage_km: { min: 10, max: 20 },
    
    default_posture: 'HASTY_DEFENSE',
    default_mission: 'DEFENSE',
    operational_state: 'COMBAT_ENGAGED',
    
    typical_strength: 4400,  // Updated to match IBCT composition
    
    battalion_composition: {
      INFANTRY: 3,
      ARTILLERY: 1,
      ENGINEERS: 1
    },
    
    // -------------------------------------------------------------------------
    // DOCTRINAL COMPOSITION (v3.0 - Phase 1)
    // Infantry BCT - light, dismounted focus
    // Source: US Army IBCT Organization
    // -------------------------------------------------------------------------
    doctrinal_composition: {
      source: 'US Army Infantry BCT Organization (2016-2023)',
      reference: 'FM 3-21.10, CBO Force Structure Tool',
      
      // Brigade-level composition
      brigade: {
        total_strength: 4400,
        sub_units: [
          {
            type: 'BATTALION',
            unit_type: 'INFANTRY',
            count: 3,
            strength_each: 800,
            name_pattern: '{parent} Infantry Battalion {index}',
            notes: 'Light infantry battalions - dismounted operations'
          },
          {
            type: 'BATTALION',
            unit_type: 'ARTILLERY',
            count: 1,
            strength_each: 500,
            name_pattern: '{parent} Field Artillery Battalion',
            notes: 'Towed 105mm or 155mm howitzers (lighter than ABCT)'
          },
          {
            type: 'BATTALION',
            unit_type: 'ENGINEERS',
            count: 1,
            strength_each: 600,
            name_pattern: '{parent} Brigade Engineer Battalion',
            notes: 'Light engineer battalion'
          },
          {
            type: 'BATTALION',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 700,
            name_pattern: '{parent} Brigade Support Battalion',
            notes: 'Logistics support'
          },
          {
            type: 'SQUADRON',
            unit_type: 'RECONNAISSANCE',
            count: 1,
            strength_each: 400,
            name_pattern: '{parent} Cavalry Squadron',
            notes: 'Reconnaissance - may include light vehicles'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Brigade HQ'
          },
          {
            type: 'COMPANY',
            unit_type: 'SIGNAL',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Signal Company',
            notes: 'Communications support'
          },
          {
            type: 'COMPANY',
            unit_type: 'MILITARY_INTELLIGENCE',
            count: 1,
            strength_each: 200,
            name_pattern: '{parent} Military Intelligence Company',
            notes: 'Intelligence and surveillance'
          }
        ]
      },
      
      // Battalion-level composition (infantry battalion)
      battalion: {
        total_strength: 800,
        sub_units: [
          {
            type: 'COMPANY',
            unit_type: 'INFANTRY',
            count: 3,
            strength_each: 150,
            name_pattern: '{parent} Company {letter}',
            notes: 'Rifle companies - dismounted'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Battalion HQ and weapons platoon'
          },
          {
            type: 'COMPANY',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 150,
            name_pattern: '{parent} Forward Support Company',
            notes: 'Battalion logistics'
          },
          {
            type: 'COMPANY',
            unit_type: 'ENGINEERS',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Engineer Company',
            notes: 'Organic engineers (IBCT has organic engineers)'
          }
        ]
      }
    },
    
    medical: {
      role_2_basic: 1,
      role_1_stations: 5,
      ambulances: 16,
      medevac_organic: false
    },
    
    description: 'Dismounted infantry - highest casualty rates'
  },
  
  MECHANIZED: {
    value: 'MECHANIZED',
    label: 'Mechanized',
    icon: '🚜',
    color: '#4caf50',
    category: 'COMBAT',
    
    casualty_modifier: 1.0,
    
    ukraine_data: {
      base_rate: 1.0,
      drone_vulnerability: 1.3,
      artillery_vulnerability: 1.2,
      
      wia_kia_ratio: 3.0,
      casualty_notes: 'Balanced protection and mobility'
    },
    
    has_frontage: true,
    typical_position_km: 4,
    frontage_km: { min: 12, max: 25 },
    
    default_posture: 'DELIBERATE_DEFENSE',
    default_mission: 'DEFENSE',
    operational_state: 'COMBAT_ENGAGED',
    
    typical_strength: 4700,  // Updated from 5000 to match doctrinal composition
    
    battalion_composition: {
      MECHANIZED: 2,
      ARMOR: 1,
      ARTILLERY: 1,
      ENGINEERS: 1
    },
    
    // -------------------------------------------------------------------------
    // DOCTRINAL COMPOSITION (v3.0 - Phase 1)
    // Auto-generated sub-units for accurate casualty calculations
    // Source: ATP-3.2.1, FM 3-96 BCT Organization (2016), CBO Force Structure Tool
    // -------------------------------------------------------------------------
    doctrinal_composition: {
      source: 'US Army BCT Organization (2016-2023)',
      reference: 'ATP-3.2.1, FM 3-96, CBO Interactive Force Structure Tool 2023',
      
      // Brigade-level composition (when echelon=DIVISION)
      brigade: {
        total_strength: 4700,
        sub_units: [
          {
            type: 'BATTALION',
            unit_type: 'MECHANIZED',
            count: 2,
            strength_each: 800,
            name_pattern: '{parent} Combined Arms Battalion {index}',
            notes: 'Combined arms battalions with mix of mechanized infantry and armor companies'
          },
          {
            type: 'BATTALION',
            unit_type: 'ARMOR',
            count: 1,
            strength_each: 800,
            name_pattern: '{parent} Combined Arms Battalion {index}',
            notes: 'Tank-heavy combined arms battalion'
          },
          {
            type: 'BATTALION',
            unit_type: 'ARTILLERY',
            count: 1,
            strength_each: 500,
            name_pattern: '{parent} Field Artillery Battalion',
            notes: 'Direct support artillery (18x M109A7 Paladin 155mm howitzers)'
          },
          {
            type: 'BATTALION',
            unit_type: 'ENGINEERS',
            count: 1,
            strength_each: 600,
            name_pattern: '{parent} Brigade Engineer Battalion',
            notes: 'Combat engineers - mobility/counter-mobility/survivability'
          },
          {
            type: 'BATTALION',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 700,
            name_pattern: '{parent} Brigade Support Battalion',
            notes: 'Logistics - supply, maintenance, medical, transport'
          },
          {
            type: 'SQUADRON',
            unit_type: 'RECONNAISSANCE',
            count: 1,
            strength_each: 400,
            name_pattern: '{parent} Cavalry Squadron',
            notes: 'Reconnaissance and security - 3x cavalry troops'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Brigade HQ - command, control, staff'
          }
        ]
      },
      
      // Battalion-level composition (when echelon=BRIGADE, user builds battalions)
      battalion: {
        total_strength: 800,
        sub_units: [
          {
            type: 'COMPANY',
            unit_type: 'MECHANIZED',
            count: 2,
            strength_each: 150,
            name_pattern: '{parent} Company {letter}',
            notes: 'M2 Bradley mechanized infantry companies'
          },
          {
            type: 'COMPANY',
            unit_type: 'ARMOR',
            count: 2,
            strength_each: 150,
            name_pattern: '{parent} Company {letter}',
            notes: 'M1 Abrams tank companies'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Battalion HQ and command staff'
          },
          {
            type: 'COMPANY',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 150,
            name_pattern: '{parent} Forward Support Company',
            notes: 'Attached from brigade support battalion'
          }
        ]
      }
    },
    
    medical: {
      role_2_basic: 1,
      role_1_stations: 5,
      ambulances: 16,
      tracked_ambulances: 6,
      medevac_organic: false
    },
    
    description: 'Infantry with IFV/APC - balanced force'
  },
  
  AIRBORNE: {
    value: 'AIRBORNE',
    label: 'Airborne',
    icon: '🪂',
    color: '#ff9800',
    category: 'COMBAT',
    
    casualty_modifier: 1.1,
    
    ukraine_data: {
      base_rate: 1.15,
      drone_vulnerability: 1.2,
      artillery_vulnerability: 1.3,
      
      wia_kia_ratio: 3.2,
      casualty_notes: 'Light forces, often elite but exposed'
    },
    
    has_frontage: true,
    typical_position_km: 2,
    
    default_posture: 'HASTY_DEFENSE',
    default_mission: 'DEFENSE',
    operational_state: 'COMBAT_ENGAGED',
    
    typical_strength: 4000,
    
    battalion_composition: {
      INFANTRY: 3,
      ARTILLERY: 1,
      LOGISTICS: 1
    },

    // -------------------------------------------------------------------------
    // DOCTRINAL COMPOSITION (v2.9 - BUG FIX)
    // Airborne / parachute brigade — light, air-portable, no tracked assets
    // Source: FM 3-21.10 Airborne / Air Assault; 11e Brigade Parachutiste (France);
    //   82nd Airborne Division (US); 16 Air Assault Brigade (UK)
    // -------------------------------------------------------------------------
    doctrinal_composition: {
      source: 'NATO Airborne Brigade Organization (generic)',
      reference: 'FM 3-21.10; French 11e BP; UK 16 Air Assault Bde; ATP 3-18',

      brigade: {
        total_strength: 4000,
        sub_units: [
          {
            type: 'BATTALION',
            unit_type: 'INFANTRY',
            count: 3,
            strength_each: 800,
            name_pattern: '{parent} Parachute Battalion {index}',
            notes: 'Light parachute infantry — dismounted, air-portable'
          },
          {
            type: 'BATTALION',
            unit_type: 'ARTILLERY',
            count: 1,
            strength_each: 500,
            name_pattern: '{parent} Artillery Battalion',
            notes: 'Light towed artillery (105mm) — air-droppable'
          },
          {
            type: 'BATTALION',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 450,
            name_pattern: '{parent} Support Battalion',
            notes: 'Airborne logistics — air-portable supply and maintenance'
          },
          {
            type: 'SQUADRON',
            unit_type: 'RECONNAISSANCE',
            count: 1,
            strength_each: 150,
            name_pattern: '{parent} Reconnaissance Squadron',
            notes: 'Light airborne recce — dismounted or light vehicle'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Brigade HQ — C2 element, air-portable'
          }
        ]
      },

      battalion: {
        total_strength: 800,
        sub_units: [
          {
            type: 'COMPANY',
            unit_type: 'INFANTRY',
            count: 3,
            strength_each: 150,
            name_pattern: '{parent} Company {letter}',
            notes: 'Parachute infantry companies — fully dismounted'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Battalion HQ and fire support element'
          },
          {
            type: 'COMPANY',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 150,
            name_pattern: '{parent} Support Company',
            notes: 'Airborne CSS — lighter than standard'
          }
        ]
      }
    },

    medical: {
      role_2_basic: 1,
      role_1_stations: 4,
      ambulances: 12,
      air_portable: true,
      medevac_organic: false
    },
    
    description: 'Parachute infantry - light, elite'
  },
  
  AIR_ASSAULT: {
    value: 'AIR_ASSAULT',
    label: 'Air Assault',
    icon: '🚁',
    color: '#ff9800',
    category: 'COMBAT',
    
    casualty_modifier: 1.0,
    
    ukraine_data: {
      base_rate: 1.1,
      drone_vulnerability: 1.1,
      artillery_vulnerability: 1.2,
      
      wia_kia_ratio: 3.0,
      casualty_notes: 'Helicopter-mobile infantry'
    },
    
    has_frontage: true,
    typical_position_km: 2,
    
    default_posture: 'HASTY_DEFENSE',
    default_mission: 'DEFENSE',
    operational_state: 'COMBAT_ENGAGED',
    
    typical_strength: 4500,
    
    battalion_composition: {
      INFANTRY: 3,
      AVIATION: 1,
      ARTILLERY: 1
    },

    // -------------------------------------------------------------------------
    // DOCTRINAL COMPOSITION (v2.10 - UT-GAP-003)
    // Air Assault Brigade — helicopter-mobile infantry with organic aviation
    // Source: US 101st Airborne Division (Air Assault); UK 16 Air Assault Bde;
    //   FM 3-04 Army Aviation; ATP 3-18 Air Assault Operations
    // -------------------------------------------------------------------------
    doctrinal_composition: {
      source: 'NATO Air Assault Brigade Organization (generic)',
      reference: 'FM 3-04; ATP 3-18; 101st ABN (AA); UK 16 Air Assault Bde',

      brigade: {
        total_strength: 4500,
        sub_units: [
          {
            type: 'BATTALION',
            unit_type: 'INFANTRY',
            count: 3,
            strength_each: 800,
            name_pattern: '{parent} Air Assault Battalion {index}',
            notes: 'Air assault infantry — helicopter-insertable, dismounted ops'
          },
          {
            type: 'BATTALION',
            unit_type: 'AVIATION',
            count: 1,
            strength_each: 400,
            name_pattern: '{parent} Aviation Battalion',
            notes: 'Organic lift and attack aviation — UH-60/AH-64 (US); Lynx/Apache (UK)'
          },
          {
            type: 'BATTALION',
            unit_type: 'ARTILLERY',
            count: 1,
            strength_each: 450,
            name_pattern: '{parent} Artillery Battalion',
            notes: 'Light towed artillery (105mm) — underslung helicopter delivery'
          },
          {
            type: 'BATTALION',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 500,
            name_pattern: '{parent} Support Battalion',
            notes: 'Air assault CSS — forward arming and refueling point (FARP) operations'
          },
          {
            type: 'SQUADRON',
            unit_type: 'RECONNAISSANCE',
            count: 1,
            strength_each: 200,
            name_pattern: '{parent} Cavalry Squadron',
            notes: 'Air cavalry reconnaissance — light scouts and OH-58/AH variant'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 150,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Brigade HQ — air assault C2'
          }
        ]
      },

      battalion: {
        total_strength: 800,
        sub_units: [
          {
            type: 'COMPANY',
            unit_type: 'INFANTRY',
            count: 3,
            strength_each: 160,
            name_pattern: '{parent} Company {letter}',
            notes: 'Air assault infantry companies — fully air-portable'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Battalion HQ and fire support element'
          },
          {
            type: 'COMPANY',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 120,
            name_pattern: '{parent} Forward Support Company',
            notes: 'Air-portable CSS — FARP coordination'
          }
        ]
      }
    },

    medical: {
      role_2_basic: 1,
      role_1_stations: 4,
      ambulances: 12,
      medevac_aircraft: 4,
      medevac_organic: true  // Exception - organic aviation
    },
    
    description: 'Helicopter-mobile infantry with organic aviation'
  },
  
  MOUNTAIN: {
    value: 'MOUNTAIN',
    label: 'Mountain',
    icon: '🏔️',
    color: '#795548',
    category: 'COMBAT',
    
    casualty_modifier: 1.15,
    
    ukraine_data: {
      base_rate: 1.2,
      drone_vulnerability: 0.9,      // Terrain provides cover
      artillery_vulnerability: 0.8,   // Dispersed
      dnbi_rate: 1.5,                // Environmental casualties
      
      wia_kia_ratio: 3.0,
      casualty_notes: 'High DNBI from terrain/environment'
    },
    
    has_frontage: true,
    typical_position_km: 2,
    
    default_posture: 'DELIBERATE_DEFENSE',
    default_mission: 'DEFENSE',
    operational_state: 'COMBAT_ENGAGED',
    
    typical_strength: 4000,
    
    battalion_composition: {
      INFANTRY: 3,
      ARTILLERY: 1,
      ENGINEERS: 1
    },

    // -------------------------------------------------------------------------
    // DOCTRINAL COMPOSITION (v2.9 - BUG FIX)
    // Mountain infantry brigade — lighter than standard infantry; animal transport;
    // altitude medicine capability; dispersed HQ positioning
    // Source: 27e Brigade d'Infanterie de Montagne (France); Gebirgsjäger (Germany);
    //   Romanian Mountain Troops; Italian Alpini Brigade; FM 3-21.10 mountain annex
    // -------------------------------------------------------------------------
    doctrinal_composition: {
      source: 'NATO Mountain Brigade Organization (generic)',
      reference: 'FM 3-21.10 mountain annex; 27e BIM (France); Gebirgsjägerbrigade (Germany)',

      brigade: {
        total_strength: 4000,
        sub_units: [
          {
            type: 'BATTALION',
            unit_type: 'INFANTRY',
            count: 3,
            strength_each: 800,
            name_pattern: '{parent} Mountain Battalion {index}',
            notes: 'Mountain infantry — ski/high altitude qualified, pack animals organic'
          },
          {
            type: 'BATTALION',
            unit_type: 'ARTILLERY',
            count: 1,
            strength_each: 450,
            name_pattern: '{parent} Mountain Artillery Battalion',
            notes: 'Light towed mountain artillery (105mm) — mule-portable sections'
          },
          {
            type: 'BATTALION',
            unit_type: 'ENGINEERS',
            count: 1,
            strength_each: 500,
            name_pattern: '{parent} Mountain Engineer Battalion',
            notes: 'Mountain engineers — route opening, bridging, avalanche clearance'
          },
          {
            type: 'BATTALION',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 400,
            name_pattern: '{parent} Support Battalion',
            notes: 'Mountain logistics — pack animal transport, rotary-wing resupply'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Brigade HQ — dispersed C2 for mountain terrain'
          }
        ]
      },

      battalion: {
        total_strength: 800,
        sub_units: [
          {
            type: 'COMPANY',
            unit_type: 'INFANTRY',
            count: 3,
            strength_each: 160,
            name_pattern: '{parent} Company {letter}',
            notes: 'Mountain infantry companies — ski-qualified, pack animal organic'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Battalion HQ and altitude medical element'
          },
          {
            type: 'COMPANY',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 120,
            name_pattern: '{parent} Support Company',
            notes: 'Mountain CSS — mule teams, rotary-wing resupply coordination'
          }
        ]
      }
    },

    medical: {
      role_2_basic: 1,
      role_1_stations: 4,
      ambulances: 10,
      mule_teams: 4,          // Animal transport
      altitude_medicine: true,
      medevac_organic: false
    },
    
    description: 'Mountain warfare specialists - high DNBI'
  },
  
  // -------------------------------------------------------------------------
  // COMBAT SUPPORT BRIGADE TYPES
  // -------------------------------------------------------------------------
  ARTILLERY: {
    value: 'ARTILLERY',
    label: 'Artillery',
    icon: '💥',
    color: '#f44336',
    category: 'SUPPORT',
    
    casualty_modifier: 0.4, // 2026-10-11: was 0.6 (support brigade, rear exposure scaled by about 0.7; EDUCATED GUESS)
    
    ukraine_data: {
      base_rate: 0.7,
      drone_vulnerability: 1.6,
      artillery_vulnerability: 1.8,
      
      wia_kia_ratio: 2.8,
      casualty_notes: 'Counter-battery primary threat'
    },
    
    has_frontage: false,
    typical_position_km: 20,   // v2.8 FIX: was -15 (sign error) — artillery brigades positioned behind front
    
    default_posture: 'DELIBERATE_DEFENSE',
    default_mission: 'SUPPORT',
    operational_state: 'SUPPORT_ACTIVE',
    
    typical_strength: 2600,  // Updated to match composition
    
    battalion_composition: {
      ARTILLERY: 4,
      LOGISTICS: 1
    },
    
    // -------------------------------------------------------------------------
    // DOCTRINAL COMPOSITION (v3.0 - Phase 1)
    // Artillery Brigade - fires support
    // Source: FM 3-09 Field Artillery
    // -------------------------------------------------------------------------
    doctrinal_composition: {
      source: 'US Army Artillery Brigade Organization',
      reference: 'FM 3-09 Field Artillery Operations and Fire Support (2022)',
      
      // Brigade-level composition
      brigade: {
        total_strength: 2600,
        sub_units: [
          {
            type: 'BATTALION',
            unit_type: 'ARTILLERY',
            count: 4,
            strength_each: 500,
            name_pattern: '{parent} Field Artillery Battalion {index}',
            notes: 'Fires battalions - 155mm or MLRS'
          },
          {
            type: 'BATTALION',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 400,
            name_pattern: '{parent} Support Battalion',
            notes: 'High ammunition consumption requires dedicated logistics'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Brigade HQ and fire direction center'
          },
          {
            type: 'COMPANY',
            unit_type: 'SIGNAL',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Signal Company',
            notes: 'Fire support communications network'
          }
        ]
      },
      
      // Battalion-level composition (artillery battalion)
      battalion: {
        total_strength: 500,
        sub_units: [
          {
            type: 'BATTERY',
            unit_type: 'ARTILLERY',
            count: 3,
            strength_each: 120,
            name_pattern: '{parent} Battery {letter}',
            notes: 'Firing batteries - 6x M109 or 6x HIMARS'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 80,
            name_pattern: '{parent} Headquarters Battery',
            notes: 'Battalion HQ and fire direction center'
          },
          {
            type: 'COMPANY',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 80,
            name_pattern: '{parent} Service Battery',
            notes: 'Ammunition resupply and maintenance'
          }
        ]
      }
    },
    
    medical: {
      role_2_basic: 1,
      role_1_stations: 5,
      ambulances: 8,
      medevac_organic: false
    },
    
    description: 'Fires brigade - counter-battery threat'
  },
  
  AVIATION: {
    value: 'AVIATION',
    label: 'Aviation',
    icon: '🚁',
    color: '#2196f3',
    category: 'SUPPORT',
    
    casualty_modifier: 0.3, // 2026-10-11: was 0.4 (support brigade, rear exposure scaled by about 0.7; EDUCATED GUESS)
    
    ukraine_data: {
      base_rate: 0.5,
      drone_vulnerability: 0.8,
      air_defense_vulnerability: 2.5,
      
      wia_kia_ratio: 1.5,
      casualty_notes: 'Low casualties but high per-incident lethality'
    },
    
    has_frontage: false,
    typical_position_km: 25,   // v2.8 FIX: was -25 (sign error) — aviation brigades operate from airfields behind FEBA
    
    default_posture: 'RESERVE',
    default_mission: 'SUPPORT',
    operational_state: 'SUPPORT_ACTIVE',
    
    typical_strength: 3000,

    // -------------------------------------------------------------------------
    // DOCTRINAL COMPOSITION (v2.10 - UT-GAP-003)
    // Combat Aviation Brigade — attack, assault, general support aviation
    // Source: FM 3-04 Army Aviation; US CAB organization; NATO aviation brigade
    // -------------------------------------------------------------------------
    doctrinal_composition: {
      source: 'US Combat Aviation Brigade Organization (generic)',
      reference: 'FM 3-04; ATP 3-04.1; US CAB MTOE 2018',

      brigade: {
        total_strength: 3000,
        sub_units: [
          {
            type: 'BATTALION',
            unit_type: 'AVIATION',
            count: 2,
            strength_each: 700,
            name_pattern: '{parent} Attack/Assault Battalion {index}',
            notes: 'Attack-assault battalions — AH-64 Apache and UH-60 mix'
          },
          {
            type: 'BATTALION',
            unit_type: 'AVIATION',
            count: 1,
            strength_each: 600,
            name_pattern: '{parent} General Support Aviation Battalion',
            notes: 'General support — CH-47 Chinook heavy lift and MEDEVAC'
          },
          {
            type: 'BATTALION',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 500,
            name_pattern: '{parent} Aviation Support Battalion',
            notes: 'ASB — aircraft maintenance, FARP ops, supply'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 200,
            name_pattern: '{parent} Headquarters Company',
            notes: 'CAB HQ — airspace C2, liaison'
          }
        ]
      },

      battalion: {
        total_strength: 700,
        sub_units: [
          {
            type: 'COMPANY',
            unit_type: 'AVIATION',
            count: 3,
            strength_each: 150,
            name_pattern: '{parent} Company {letter}',
            notes: 'Aviation companies — 8 aircraft each'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Battalion HQ and maintenance platoon'
          },
          {
            type: 'COMPANY',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Forward Support Company',
            notes: 'Aviation FSC — forward maintenance and supply'
          }
        ]
      }
    },

    medical: {
      role_2_basic: 1,
      flight_surgeons: 4,
      ambulances: 6,
      medevac_aircraft: 8,
      sar_teams: 2,
      medevac_organic: true
    },

    description: 'Helicopter aviation - provides MEDEVAC'
  },
  
  // -------------------------------------------------------------------------
  // COMBAT SERVICE SUPPORT BRIGADE TYPES
  // -------------------------------------------------------------------------
  LOGISTICS: {
    value: 'LOGISTICS',
    label: 'Logistics (NATO)',
    icon: '📦',
    color: '#9e9e9e',
    category: 'CSS',
    
    casualty_modifier: 0.2, // 2026-10-11: was 0.3 (support brigade, rear exposure scaled by about 0.7; EDUCATED GUESS)
    
    ukraine_data: {
      base_rate: 0.35,
      drone_vulnerability: 1.4,
      artillery_vulnerability: 0.6,
      
      wia_kia_ratio: 4.0,
      casualty_notes: 'Convoy and depot attacks'
    },
    
    has_frontage: false,
    typical_position_km: 40,   // v2.8 FIX: was -40 (sign error) — logistics brigades in deep rear area
    
    default_posture: 'RESERVE',
    default_mission: 'SUPPORT',
    operational_state: 'TRUE_RESERVE',
    
    typical_strength: 3200,  // Updated to match composition
    
    battalion_composition: {
      LOGISTICS: 3,
      MAINTENANCE: 1,
      MEDICAL: 1
    },
    
    // -------------------------------------------------------------------------
    // DOCTRINAL COMPOSITION (v3.0 - Phase 1)
    // NATO/Romanian-style Logistics Brigade
    // More separated functions than US Sustainment Brigade
    // -------------------------------------------------------------------------
    doctrinal_composition: {
      source: 'NATO Logistics Brigade Organization (European model)',
      reference: 'ATP 4.1 Allied Joint Doctrine for Logistics',
      
      // Brigade-level composition
      brigade: {
        total_strength: 3200,
        sub_units: [
          {
            type: 'BATTALION',
            unit_type: 'TRANSPORT',
            count: 2,
            strength_each: 800,
            name_pattern: '{parent} Transport Battalion {index}',
            notes: 'Line haul and convoy operations'
          },
          {
            type: 'BATTALION',
            unit_type: 'SUPPLY',
            count: 1,
            strength_each: 600,
            name_pattern: '{parent} Supply Battalion',
            notes: 'Class I-IX supply distribution and storage'
          },
          {
            type: 'BATTALION',
            unit_type: 'MAINTENANCE',
            count: 1,
            strength_each: 600,
            name_pattern: '{parent} Maintenance Battalion',
            notes: 'Field maintenance and recovery'
          },
          {
            type: 'BATTALION',
            unit_type: 'MEDICAL_SUPPORT',
            count: 1,
            strength_each: 400,
            name_pattern: '{parent} Medical Battalion',
            notes: 'Role 2 medical support'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 200,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Brigade HQ and coordination'
          }
        ]
      },
      
      // Battalion-level composition (transport battalion example)
      battalion: {
        total_strength: 800,
        sub_units: [
          {
            type: 'COMPANY',
            unit_type: 'TRANSPORT',
            count: 3,
            strength_each: 200,
            name_pattern: '{parent} Company {letter}',
            notes: 'Heavy truck companies'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Battalion HQ'
          },
          {
            type: 'COMPANY',
            unit_type: 'MAINTENANCE',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Maintenance Company',
            notes: 'Vehicle maintenance'
          }
        ]
      }
    },
    
    medical: {
      role_2_basic: 0,
      role_1_stations: 5,
      ambulances: 8,
      medevac_organic: false
    },
    
    description: 'Supply and transportation - rear area'
  },
  
  MEDICAL: {
    value: 'MEDICAL',
    label: 'Medical',
    icon: '🏥',
    color: '#e91e63',
    category: 'CSS',
    
    casualty_modifier: 0.15, // 2026-10-11: was 0.2 (support brigade, rear exposure scaled by about 0.7; EDUCATED GUESS)
    is_medical_provider: true,
    
    ukraine_data: {
      base_rate: 0.25,
      drone_vulnerability: 1.2,
      artillery_vulnerability: 0.4,
      
      wia_kia_ratio: 4.0,
      casualty_notes: 'Protected status limited value'
    },
    
    has_frontage: false,
    typical_position_km: 30,   // v2.8 FIX: was -30 (sign error) — medical brigades protected but accessible
    
    default_posture: 'RESERVE',
    default_mission: 'SUPPORT',
    operational_state: 'TRUE_RESERVE',
    
    typical_strength: 2500,
    
    // Medical brigades PROVIDE support, don't have organic combat battalions
    medical: {
      provides_role: 'ROLE_2_ENHANCED',
      role_2_enhanced: 1,
      role_2_basic: 3,
      ambulance_companies: 3,
      surgical_teams: 4,
      medevac_aircraft: 0,  // Allocated from aviation brigade
      
      supported_strength: 25000  // Can support a division
    },
    
    description: 'Medical support brigade - provides Role 2/3'
  },
  
  // =========================================================================
  // v2.5.0 BUG-FIX: Added missing brigade types to UNIT_TYPES
  // These were in BATTALION_COMPOSITION_BY_BRIGADE_TYPE but not here!
  // Calculator uses getCasualtyModifier() which looks up in UNIT_TYPES first.
  // =========================================================================
  
  ARMOR: {
    value: 'ARMOR',
    label: 'Armored',
    icon: '🛡️',
    color: '#4caf50',
    category: 'COMBAT',
    
    casualty_modifier: 0.95,  // Same as HEAVY - tank-centric
    
    ukraine_data: {
      base_rate: 0.95,
      drone_vulnerability: 1.5,  // FPV drones devastate tanks
      artillery_vulnerability: 1.0,
      atgm_vulnerability: 1.8,
      wia_kia_ratio: 2.0,        // Tank kills often catastrophic
      vehicle_focus: true,
      casualty_notes: 'FPV drones and ATGMs cause catastrophic vehicle kills'
    },
    
    has_frontage: true,
    typical_position_km: 5,
    frontage_km: { min: 15, max: 30 },
    
    default_posture: 'DELIBERATE_DEFENSE',
    default_mission: 'DEFENSE',
    operational_state: 'COMBAT_ENGAGED',
    
    typical_strength: 4700,  // Updated to match doctrinal composition
    
    // -------------------------------------------------------------------------
    // DOCTRINAL COMPOSITION (v3.0 - Phase 1)
    // Armored BCT - tank-heavy configuration
    // Source: US Army ABCT Organization (2016-2023)
    // -------------------------------------------------------------------------
    doctrinal_composition: {
      source: 'US Army Armored BCT Organization (2016-2023)',
      reference: 'FM 3-96, Army 2030 Divisional Templates (April 2023)',
      
      // Brigade-level composition
      brigade: {
        total_strength: 4700,
        sub_units: [
          {
            type: 'BATTALION',
            unit_type: 'ARMOR',
            count: 2,
            strength_each: 800,
            name_pattern: '{parent} Combined Arms Battalion {index}',
            notes: 'Tank-heavy combined arms battalions (2x tank companies, 1x mech infantry company)'
          },
          {
            type: 'BATTALION',
            unit_type: 'MECHANIZED',
            count: 1,
            strength_each: 800,
            name_pattern: '{parent} Combined Arms Battalion {index}',
            notes: 'Infantry-heavy combined arms battalion (2x mech companies, 1x tank company)'
          },
          {
            type: 'BATTALION',
            unit_type: 'ARTILLERY',
            count: 1,
            strength_each: 500,
            name_pattern: '{parent} Field Artillery Battalion',
            notes: 'Direct support artillery (18x M109A7 Paladin)'
          },
          {
            type: 'BATTALION',
            unit_type: 'ENGINEERS',
            count: 1,
            strength_each: 600,
            name_pattern: '{parent} Brigade Engineer Battalion',
            notes: 'Combat engineers with armored breaching capability'
          },
          {
            type: 'BATTALION',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 700,
            name_pattern: '{parent} Brigade Support Battalion',
            notes: 'Heavy logistics - high fuel/ammo consumption'
          },
          {
            type: 'SQUADRON',
            unit_type: 'RECONNAISSANCE',
            count: 1,
            strength_each: 400,
            name_pattern: '{parent} Cavalry Squadron',
            notes: 'Reconnaissance with mixed armor/mechanized troops'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Brigade HQ - command and control'
          }
        ]
      },
      
      // Battalion-level composition (tank battalion)
      battalion: {
        total_strength: 800,
        sub_units: [
          {
            type: 'COMPANY',
            unit_type: 'ARMOR',
            count: 2,
            strength_each: 150,
            name_pattern: '{parent} Company {letter}',
            notes: 'M1 Abrams tank companies (14x tanks each)'
          },
          {
            type: 'COMPANY',
            unit_type: 'MECHANIZED',
            count: 2,
            strength_each: 150,
            name_pattern: '{parent} Company {letter}',
            notes: 'M2 Bradley mechanized companies'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Battalion HQ'
          },
          {
            type: 'COMPANY',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 150,
            name_pattern: '{parent} Forward Support Company',
            notes: 'High maintenance demand from tanks'
          }
        ]
      }
    },
    
    description: 'Tank-heavy armored brigade (alias for HEAVY)'
  },
  
  STRYKER: {
    value: 'STRYKER',
    label: 'Stryker',
    icon: '🚗',
    color: '#2196f3',
    category: 'COMBAT',
    
    casualty_modifier: 1.05,  // Medium-weight, less protection than heavy
    
    ukraine_data: {
      base_rate: 1.05,
      drone_vulnerability: 1.3,
      artillery_vulnerability: 1.2,
      wia_kia_ratio: 3.2,
      casualty_notes: 'Medium protection, wheeled mobility'
    },
    
    has_frontage: true,
    typical_position_km: 4,
    frontage_km: { min: 12, max: 25 },
    
    default_posture: 'HASTY_DEFENSE',
    default_mission: 'DEFENSE',
    operational_state: 'COMBAT_ENGAGED',
    
    typical_strength: 4500,

    // -------------------------------------------------------------------------
    // DOCTRINAL COMPOSITION (v2.10 - UT-GAP-003)
    // Stryker BCT — US Army medium-weight wheeled maneuver brigade
    // Source: FM 3-21.31 The Stryker Brigade Combat Team; US Army SBCT org (2016)
    // -------------------------------------------------------------------------
    doctrinal_composition: {
      source: 'US Army Stryker BCT Organization (2016–2023)',
      reference: 'FM 3-21.31; US Army CBO Force Structure Tool 2023',

      brigade: {
        total_strength: 4500,
        sub_units: [
          {
            type: 'BATTALION',
            unit_type: 'INFANTRY',
            count: 3,
            strength_each: 900,
            name_pattern: '{parent} Infantry Battalion {index}',
            notes: 'Stryker infantry battalions — ICV-mounted, dismount-capable'
          },
          {
            type: 'BATTALION',
            unit_type: 'ARTILLERY',
            count: 1,
            strength_each: 500,
            name_pattern: '{parent} Field Artillery Battalion',
            notes: 'Towed 155mm howitzers — M777 or similar; lighter than ABCT'
          },
          {
            type: 'BATTALION',
            unit_type: 'ENGINEERS',
            count: 1,
            strength_each: 550,
            name_pattern: '{parent} Brigade Engineer Battalion',
            notes: 'Combat engineers — Stryker-compatible mobility/counter-mobility'
          },
          {
            type: 'BATTALION',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 650,
            name_pattern: '{parent} Brigade Support Battalion',
            notes: 'BSB — wheeled logistics optimised for SBCT operational tempo'
          },
          {
            type: 'SQUADRON',
            unit_type: 'RECONNAISSANCE',
            count: 1,
            strength_each: 350,
            name_pattern: '{parent} Cavalry Squadron',
            notes: 'Stryker reconnaissance squadron — wheeled RSTA'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Brigade HQ — command and control'
          }
        ]
      },

      battalion: {
        total_strength: 900,
        sub_units: [
          {
            type: 'COMPANY',
            unit_type: 'INFANTRY',
            count: 3,
            strength_each: 185,
            name_pattern: '{parent} Company {letter}',
            notes: 'Stryker ICV companies — 9 Strykers per company'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Battalion HQ, mortar platoon, anti-tank section'
          },
          {
            type: 'COMPANY',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 145,
            name_pattern: '{parent} Forward Support Company',
            notes: 'FSC — field maintenance, supply, medical support'
          }
        ]
      }
    },

    description: 'US Army medium-weight brigade with Stryker vehicles'
  },
  
  
  ENGINEERS: {
    value: 'ENGINEERS',
    label: 'Engineer',
    icon: '🔧',
    color: '#ff9800',
    category: 'SUPPORT',
    
    casualty_modifier: 0.5,  // Support unit, but works near FEBA
    
    ukraine_data: {
      base_rate: 0.5,
      drone_vulnerability: 1.2,
      artillery_vulnerability: 1.0,
      mine_vulnerability: 1.8,  // High mine/UXO exposure
      wia_kia_ratio: 3.0,
      casualty_notes: 'Mine/UXO clearance is extremely dangerous'
    },
    
    has_frontage: false,
    typical_position_km: 15,  // Close to FEBA for breaching support
    
    default_posture: 'RESERVE',
    default_mission: 'SUPPORT',
    operational_state: 'SUPPORT_ACTIVE',
    
    typical_strength: 2300,   // 2026-10-25: was 2800 (3 x 700 + logistics 400 + HQ 300); FM 3-34 puts the support company in the battalion

    // 2026-10-24 (engineer brigade, Dr. Tatar): the builder needs a doctrinal composition to make the brigade. 3 engineer battalions,
    // one support battalion, one HQ company, as in the template below (BATTALION_COMPOSITION_BY_BRIGADE_TYPE.ENGINEERS). Sizes: EDUCATED_GUESS, to
    // be replaced by the brigade type size research (R1-03 / R1-05).
    doctrinal_composition: {
      source: 'Combat engineer brigade, sizes assumed (EDUCATED_GUESS)',
      reference: 'none: sizes to be sourced in R1-03 / R1-05',
      brigade: {
        total_strength: 2300,
        sub_units: [
          { type: 'BATTALION', unit_type: 'ENGINEERS', count: 3, strength_each: 700, name_pattern: '{parent} Engineer Battalion {index}', notes: 'Mobility, counter-mobility, bridging' },
          { type: 'COMPANY', unit_type: 'HQ', count: 1, strength_each: 200, name_pattern: '{parent} Headquarters Company', notes: 'Brigade HQ' }
        ]
      }
    },
    
    description: 'Combat engineer brigade - mobility and counter-mobility'
  },
  
  SIGNAL: {
    value: 'SIGNAL',
    label: 'Signal',
    icon: '📡',
    color: '#9c27b0',
    category: 'CSS',
    
    casualty_modifier: 0.18, // 2026-10-11: was 0.25 (support brigade, rear exposure scaled by about 0.7; EDUCATED GUESS)  // Rear area, but EW targeting
    
    ukraine_data: {
      base_rate: 0.25,
      drone_vulnerability: 0.8,
      artillery_vulnerability: 0.5,
      electronic_targeting: 1.5,  // EW detection risk
      wia_kia_ratio: 4.0,
      casualty_notes: 'Electronic emissions make them targets for precision strike'
    },
    
    has_frontage: false,
    typical_position_km: 30,  // Deep rear
    
    default_posture: 'RESERVE',
    default_mission: 'SUPPORT',
    operational_state: 'SUPPORT_ACTIVE',
    
    typical_strength: 1600,
    
    description: 'Signal brigade - communications and C4I infrastructure'
  },
  
  AIR_DEFENSE: {
    value: 'AIR_DEFENSE',
    label: 'Air Defense',
    icon: '🛡️',
    color: '#607d8b',
    category: 'SUPPORT',
    
    casualty_modifier: 0.25, // 2026-10-11: was 0.35 (support brigade, rear exposure scaled by about 0.7; EDUCATED GUESS)  // Priority target for SEAD/DEAD
    
    ukraine_data: {
      base_rate: 0.35,
      drone_vulnerability: 1.0,
      artillery_vulnerability: 0.8,
      cruise_missile_vulnerability: 2.0,  // SEAD priority target
      wia_kia_ratio: 2.5,
      casualty_notes: 'High-priority target for enemy suppression of air defense'
    },
    
    has_frontage: false,
    typical_position_km: 20,  // Covering forward units
    
    default_posture: 'RESERVE',
    default_mission: 'SUPPORT',
    operational_state: 'SUPPORT_ACTIVE',
    
    typical_strength: 1800,

    // -------------------------------------------------------------------------
    // DOCTRINAL COMPOSITION (v2.10 - UT-GAP-003)
    // Air Defense Brigade — SHORAD/HIMAD protection of force and key assets
    // Source: FM 3-01 Air and Missile Defense Operations; NATO ADA brigade
    // -------------------------------------------------------------------------
    doctrinal_composition: {
      source: 'NATO Air Defense Brigade Organization (generic)',
      reference: 'FM 3-01; ATP 3-01.8; NATO IAMD doctrine',

      brigade: {
        total_strength: 1800,
        sub_units: [
          {
            type: 'BATTALION',
            unit_type: 'AIR_DEFENSE',
            count: 3,
            strength_each: 450,
            name_pattern: '{parent} Air Defense Battalion {index}',
            notes: 'Mixed SHORAD/HIMAD battalions — Patriot/NASAMS/HAWK or equivalent'
          },
          {
            type: 'BATTALION',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 250,
            name_pattern: '{parent} Maintenance Battalion',
            notes: 'ADA maintenance and supply — radar/missile system support'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 150,
            name_pattern: '{parent} Headquarters Company',
            notes: 'Brigade ADA operations center — airspace coordination'
          }
        ]
      },

      battalion: {
        total_strength: 450,
        sub_units: [
          {
            type: 'BATTERY',
            unit_type: 'AIR_DEFENSE',
            count: 3,
            strength_each: 100,
            name_pattern: '{parent} Battery {letter}',
            notes: 'ADA firing batteries — 2–4 launchers each'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Battery',
            notes: 'Battalion fire control, radar section, comms'
          },
          {
            type: 'COMPANY',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 50,
            name_pattern: '{parent} Support Battery',
            notes: 'Maintenance and supply for ADA systems'
          }
        ]
      }
    },

    description: 'Air Defense Artillery (ADA) brigade - SHORAD and HIMAD'
  },
  
  RECONNAISSANCE: {
    value: 'RECONNAISSANCE',
    label: 'Cavalry',
    icon: '🐎',
    color: '#ffc107',
    category: 'COMBAT',
    
    casualty_modifier: 0.85,  // Forward reconnaissance, high exposure
    
    ukraine_data: {
      base_rate: 0.85,
      drone_vulnerability: 1.4,
      artillery_vulnerability: 1.1,
      wia_kia_ratio: 2.8,
      casualty_notes: 'Forward operations expose them to contact'
    },
    
    has_frontage: true,
    typical_position_km: 0,  // FORWARD of main body
    frontage_km: { min: 20, max: 50 },  // Wide screening frontage
    
    default_posture: 'MOVEMENT_TO_CONTACT',
    default_mission: 'RECONNAISSANCE',
    operational_state: 'COMBAT_ENGAGED',
    
    typical_strength: 4500,

    // -------------------------------------------------------------------------
    // DOCTRINAL COMPOSITION (v2.10 - UT-GAP-003)
    // Cavalry / Reconnaissance Brigade — screening, security, intelligence
    // Source: FM 3-20 Reconnaissance and Security; US ACR/RSTA organization
    // -------------------------------------------------------------------------
    doctrinal_composition: {
      source: 'NATO Cavalry / Reconnaissance Brigade Organization (generic)',
      reference: 'FM 3-20; ATP 3-20.15; US ACR MTOE; NATO recce brigade',

      brigade: {
        total_strength: 4500,
        sub_units: [
          {
            type: 'SQUADRON',
            unit_type: 'RECONNAISSANCE',
            count: 3,
            strength_each: 1000,
            name_pattern: '{parent} Cavalry Squadron {index}',
            notes: 'Combined arms cavalry squadrons — Stryker/wheeled recce or tank/mech mix'
          },
          {
            type: 'BATTALION',
            unit_type: 'ARTILLERY',
            count: 1,
            strength_each: 500,
            name_pattern: '{parent} Artillery Battalion',
            notes: 'Direct support artillery — fires in support of recce screen'
          },
          {
            type: 'BATTALION',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 500,
            name_pattern: '{parent} Support Battalion',
            notes: 'Brigade CSS — fuel-intensive for high-tempo recce operations'
          },
          {
            type: 'COMPANY',
            unit_type: 'HQ',
            count: 1,
            strength_each: 200,
            name_pattern: '{parent} Headquarters Troop',
            notes: 'Brigade HQ — reconnaissance coordination and reporting'
          }
        ]
      },

      battalion: {
        total_strength: 1000,
        sub_units: [
          {
            type: 'TROOP',
            unit_type: 'RECONNAISSANCE',
            count: 3,
            strength_each: 220,
            name_pattern: '{parent} Troop {letter}',
            notes: 'Cavalry troops — 9–12 recce vehicles each'
          },
          {
            type: 'TROOP',
            unit_type: 'ARMOR',
            count: 1,
            strength_each: 180,
            name_pattern: '{parent} Tank Troop',
            notes: 'Tank troop — direct fire support for screening missions'
          },
          {
            type: 'TROOP',
            unit_type: 'HQ',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Headquarters Troop',
            notes: 'Squadron HQ and forward support element'
          },
          {
            type: 'COMPANY',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 150,
            name_pattern: '{parent} Forward Support Troop',
            notes: 'Fuel, ammunition, maintenance for high-tempo recce'
          }
        ]
      }
    },

    description: 'Armored Cavalry - reconnaissance and security'
  },
  
  
  // -------------------------------------------------------------------------
  // HEADQUARTERS (CORPS/DIVISION)
  // Command posts are HIGH-VALUE TARGETS - not immune to battle casualties
  // Ukraine data: HIMARS strikes, missile/drone attacks on command posts
  // -------------------------------------------------------------------------
  HQ: {
    value: 'HQ',
    label: 'Headquarters',
    icon: '⭐',
    color: '#9c27b0',
    category: 'HQ',
    
    casualty_modifier: 0.35,   // 2026-10-11: was 0.5 (rear exposure scaled by about 0.7; EDUCATED GUESS)  // Command posts suffer targeting strikes (higher than CSS 0.3, lower than combat 1.0+)
    
    ukraine_data: {
      base_rate: 0.5,
      drone_vulnerability: 2.0,    // Command posts are PRIORITY drone targets
      artillery_vulnerability: 1.5, // HIMARS/precision missile strikes on HQ
      wia_kia_ratio: 3.0,
      casualty_notes: 'Command posts suffer targeting strikes - HIMARS, missiles, drones. Not immune to battle casualties. Estimated 2-5% casualties during major operations.'
    },
    
    has_frontage: false,
    typical_position_km: 80,  // Deep rear for corps/division HQ
    
    default_posture: 'RESERVE',
    default_mission: 'COMMAND',
    operational_state: 'SUPPORT_ACTIVE',  // HQ doesn't fight directly, but IS targeted
    
    typical_strength: 2000,  // CORPS HQ personnel

    // -------------------------------------------------------------------------
    // DOCTRINAL COMPOSITION (v2.10 - UT-GAP-003)
    // Corps / Division HQ — command, control, coordination element
    // Not a maneuver brigade — no subordinate combat units.
    // Represents the HQ element when modelled as a separate force entity.
    // Source: ADP 6-0 Command and Control; FM 6-0 Commander and Staff;
    //   AJP-01 NATO Command Structure; Ukraine 2022-2024 HQ targeting data
    // -------------------------------------------------------------------------
    doctrinal_composition: {
      source: 'NATO Corps/Division HQ Organization (generic)',
      reference: 'ADP 6-0; AJP-01; FM 6-0 Commander and Staff Organization',

      brigade: {
        total_strength: 2000,
        sub_units: [
          {
            type: 'ELEMENT',
            unit_type: 'HQ',
            count: 1,
            strength_each: 800,
            name_pattern: '{parent} Main Command Post',
            notes: 'Main CP — operations, intelligence, fires, sustainment staff'
          },
          {
            type: 'ELEMENT',
            unit_type: 'HQ',
            count: 1,
            strength_each: 400,
            name_pattern: '{parent} Tactical Command Post',
            notes: 'TAC CP — forward command element, commander and key staff'
          },
          {
            type: 'ELEMENT',
            unit_type: 'SIGNAL',
            count: 1,
            strength_each: 400,
            name_pattern: '{parent} Signal Company',
            notes: 'Comms infrastructure — network, SATCOM, radio relay nodes'
          },
          {
            type: 'ELEMENT',
            unit_type: 'INFANTRY',
            count: 1,
            strength_each: 250,
            name_pattern: '{parent} HQ Security Company',
            notes: 'Force protection element — guard, reaction force for CP'
          },
          {
            type: 'ELEMENT',
            unit_type: 'LOGISTICS',
            count: 1,
            strength_each: 150,
            name_pattern: '{parent} Headquarters Support Company',
            notes: 'Admin, transport, maintenance for HQ equipment'
          }
        ]
      },

      battalion: {
        total_strength: 400,
        sub_units: [
          {
            type: 'ELEMENT',
            unit_type: 'HQ',
            count: 1,
            strength_each: 300,
            name_pattern: '{parent} Staff Section',
            notes: 'Primary staff — G2/G3/G4/G6/surgeon'
          },
          {
            type: 'ELEMENT',
            unit_type: 'SIGNAL',
            count: 1,
            strength_each: 100,
            name_pattern: '{parent} Signal Detachment',
            notes: 'Communications support'
          }
        ]
      }
    },

    description: 'Corps or Division headquarters - command and control element. High-value target for precision strikes.'
  }
};

// ============================================================================
// BATTALION COMPOSITION BY BRIGADE TYPE
// ============================================================================

export const BATTALION_COMPOSITION_BY_BRIGADE_TYPE = {
  HEAVY: {
    template: [
      { type: 'ARMOR', count: 2 },
      { type: 'MECHANIZED', count: 1 },
      { type: 'ARTILLERY', count: 1 },
      { type: 'ENGINEERS', count: 1 }
    ],
    totalBattalions: 5,
    combatBattalions: 3,
    supportBattalions: 2,
    weighted_casualty_modifier: 0.95
  },
  
  // v2.5.0 BUG-FIX: Added ARMOR as alias for HEAVY
  // Without this, ARMOR brigades fall back to DEFAULT (Infantry/Infantry/Infantry)
  ARMOR: {
    template: [
      { type: 'ARMOR', count: 2 },
      { type: 'MECHANIZED', count: 1 },
      { type: 'ARTILLERY', count: 1 },
      { type: 'ENGINEERS', count: 1 }
    ],
    totalBattalions: 5,
    combatBattalions: 3,
    supportBattalions: 2,
    weighted_casualty_modifier: 0.95,
    description: 'Armored brigade - tank-heavy combined arms',
    notes: 'Same structure as HEAVY; alias ensures ARMOR brigade type works correctly'
  },
  
  INFANTRY: {
    template: [
      { type: 'INFANTRY', count: 3 },
      { type: 'ARTILLERY', count: 1 },
      { type: 'ENGINEERS', count: 1 }
    ],
    totalBattalions: 5,
    combatBattalions: 3,
    supportBattalions: 2,
    weighted_casualty_modifier: 1.1
  },
  
  MECHANIZED: {
    template: [
      { type: 'MECHANIZED', count: 2 },
      { type: 'ARMOR', count: 1 },
      { type: 'ARTILLERY', count: 1 },
      { type: 'ENGINEERS', count: 1 }
    ],
    totalBattalions: 5,
    combatBattalions: 3,
    supportBattalions: 2,
    weighted_casualty_modifier: 1.0
  },
  
  AIRBORNE: {
    template: [
      { type: 'INFANTRY', count: 3 },
      { type: 'ARTILLERY', count: 1 },
      { type: 'LOGISTICS', count: 1 }
    ],
    totalBattalions: 5,
    combatBattalions: 3,
    supportBattalions: 2,
    weighted_casualty_modifier: 1.1
  },
  
  AIR_ASSAULT: {
    template: [
      { type: 'INFANTRY', count: 3 },
      { type: 'AVIATION', count: 1 },
      { type: 'ARTILLERY', count: 1 }
    ],
    totalBattalions: 5,
    combatBattalions: 3,
    supportBattalions: 2,
    weighted_casualty_modifier: 1.0
  },
  
  // v2.5.0 BUG-FIX: Added STRYKER brigade template (US Army)
  // Without this, STRYKER brigades fall back to DEFAULT
  STRYKER: {
    template: [
      { type: 'INFANTRY', count: 3 },      // Stryker infantry battalions
      { type: 'RECONNAISSANCE', count: 1 },       // Reconnaissance squadron
      { type: 'ARTILLERY', count: 1 },
      { type: 'ENGINEERS', count: 1 }
    ],
    totalBattalions: 6,
    combatBattalions: 4,
    supportBattalions: 2,
    weighted_casualty_modifier: 1.05,
    description: 'Stryker Brigade Combat Team (SBCT)',
    notes: 'US Army medium-weight formation; infantry-centric with wheeled APCs',
    national_variants: ['USA']
  },
  
  MOUNTAIN: {
    template: [
      { type: 'INFANTRY', count: 3 },
      { type: 'ARTILLERY', count: 1 },
      { type: 'ENGINEERS', count: 1 }
    ],
    totalBattalions: 5,
    combatBattalions: 3,
    supportBattalions: 2,
    weighted_casualty_modifier: 1.15
  },
  
  ARTILLERY: {
    template: [
      { type: 'ARTILLERY', count: 3 },
      { type: 'HQ', count: 1 }
    ],
    totalBattalions: 4,
    combatBattalions: 3,            // v2.4.0: Artillery ARE combat support, need Role 1
    supportBattalions: 1,           // Only HQ doesn't need Role 1
    weighted_casualty_modifier: 0.6,
    role1_count: 3,                 // v2.4.0: Expected Role 1 facilities
    description: 'Artillery brigade with 3 firing battalions',
    notes: 'Artillery battalions need Role 1 despite not holding frontage'
  },
  
  // ============================================================================
  // v2.2 CSS-002: Fixed LOGISTICS Brigade Template (ROU/NATO Style)
  // ============================================================================
  
  LOGISTICS: {
    template: [
      { type: 'TRANSPORT', count: 2 },      // Transport battalions (convoy ops)
      { type: 'SUPPLY', count: 1 },         // Supply battalion (depot ops)
      { type: 'MAINTENANCE', count: 1 },    // Light maintenance
      { type: 'HQ', count: 1 }              // Headquarters element
    ],
    totalBattalions: 5,
    combatBattalions: 0,
    supportBattalions: 5,
    weighted_casualty_modifier: 0.3,
    role1_count: 4,                         // v2.2: All except HQ need Role 1
    provides_role2: false,
    description: 'Logistics brigade (ROU/NATO style) - supply and transport',
    notes: 'Romanian: Brigada Logistică; transport convoys have high drone/ambush risk',
    national_variants: ['ROU', 'NATO', 'DEU']
  },
  
  // ============================================================================
  // v2.2 CSS-001: New SUSTAINMENT Brigade Template (US Style)
  // ============================================================================
  
  
  MEDICAL: {
    template: [],  // Medical brigades don't have organic battalions
    totalBattalions: 0,
    combatBattalions: 0,
    supportBattalions: 0,
    weighted_casualty_modifier: 0.2
  },
  
  // ============================================================================
  // v2.5.0 BUG-FIX: Added missing Combat Support brigade templates
  // These existed in ForceBuilder.js CSS_BRIGADE_TEMPLATES but not here
  // ============================================================================
  
  
  // Engineer brigade (2026-10-24: one key; the singular ENGINEER is a spelling of ENGINEERS, see unitTypeRegistry.js)
  ENGINEERS: {
    template: [
      { type: 'ENGINEERS', count: 3 },
      { type: 'HQ', count: 1 }
    ],
    totalBattalions: 4,   // 2026-10-25: was 5 (a logistics battalion of 400 was in the template)
    combatBattalions: 0,
    supportBattalions: 4,
    weighted_casualty_modifier: 0.5,
    role1_count: 3,
    description: 'Engineer brigade - mobility and counter-mobility',
    notes: 'Critical for river crossings, obstacle breaching, fortifications'
  },
  
  SIGNAL: {
    template: [
      { type: 'SIGNAL', count: 3 },          // Signal battalions
      { type: 'HQ', count: 1 }               // Headquarters
    ],
    totalBattalions: 4,
    combatBattalions: 0,
    supportBattalions: 4,
    weighted_casualty_modifier: 0.25,
    role1_count: 3,                          // All except HQ need Role 1
    description: 'Signal brigade - communications and C4I',
    notes: 'Provides corps/division level communications infrastructure'
  },
  
  AVIATION: {
    template: [
      { type: 'AVIATION', count: 3 },        // Attack/assault/general support helicopter battalions
      { type: 'MAINTENANCE', count: 1 },     // Aviation maintenance
      { type: 'HQ', count: 1 }               // Headquarters
    ],
    totalBattalions: 5,
    combatBattalions: 3,
    supportBattalions: 2,
    weighted_casualty_modifier: 0.4,
    role1_count: 4,                          // All except HQ need Role 1
    description: 'Combat Aviation Brigade (CAB)',
    notes: 'Attack helicopters, assault aviation, general support; high-value target for enemy AD',
    national_variants: ['USA', 'NATO']
  },
  
  AIR_DEFENSE: {
    template: [
      { type: 'AIR_DEFENSE', count: 3 },     // Air defense battalions (SHORAD/HIMAD)
      { type: 'HQ', count: 1 }               // Headquarters
    ],
    totalBattalions: 4,
    combatBattalions: 0,
    supportBattalions: 4,
    weighted_casualty_modifier: 0.35,
    role1_count: 3,                          // All except HQ need Role 1
    description: 'Air Defense Artillery (ADA) Brigade',
    notes: 'SHORAD and HIMAD systems; high-priority target for enemy SEAD/DEAD',
    national_variants: ['USA', 'NATO', 'ROU']
  },
  
  RECONNAISSANCE: {
    template: [
      { type: 'RECONNAISSANCE', count: 3 },         // Cavalry squadrons (recon)
      { type: 'ARTILLERY', count: 1 },       // Direct support artillery
      { type: 'HQ', count: 1 }               // Headquarters
    ],
    totalBattalions: 5,
    combatBattalions: 3,
    supportBattalions: 2,
    weighted_casualty_modifier: 0.85,
    role1_count: 4,                          // All except HQ need Role 1
    description: 'Armored Cavalry Regiment / Cavalry Brigade',
    notes: 'Reconnaissance and security operations; high mobility, forward operations',
    national_variants: ['USA']
  },
  
  // 2026-10-24 (naming audit step 4): HQ had no row and was built with the DEFAULT composition without saying so; the row says so (an
  // HQ is not a maneuver brigade; it is not offered as a brigade type in Tab 1). Same battalions as DEFAULT.
  HQ: {
    template: [
      { type: 'INFANTRY', count: 3 },
      { type: 'ARTILLERY', count: 1 },
      { type: 'LOGISTICS', count: 1 }
    ],
    totalBattalions: 5,
    combatBattalions: 3,
    supportBattalions: 2,
    weighted_casualty_modifier: 1.0
  },

  DEFAULT: {
    template: [
      { type: 'INFANTRY', count: 3 },
      { type: 'ARTILLERY', count: 1 },
      { type: 'LOGISTICS', count: 1 }
    ],
    totalBattalions: 5,
    combatBattalions: 3,
    supportBattalions: 2,
    weighted_casualty_modifier: 1.0
  }
};

// ============================================================================
// UNIT TYPE CATEGORIES
// ============================================================================

export const UNIT_TYPE_CATEGORIES = {
  COMBAT: {
    label: 'Combat/Maneuver',
    description: 'Units that close with and destroy the enemy',
    types: ['HEAVY', 'INFANTRY', 'MECHANIZED', 'AIRBORNE', 'AIR_ASSAULT', 'MOUNTAIN'],
    typical_casualty_modifier: 1.0,
    has_frontage: true
  },
  SUPPORT: {
    label: 'Combat Support',
    description: 'Units that provide fire support and enablers',
    types: ['ARTILLERY', 'AVIATION'],
    typical_casualty_modifier: 0.5,
    has_frontage: false
  },
  CSS: {
    label: 'Combat Service Support',
    description: 'Units that provide logistics and sustainment',
    types: ['LOGISTICS', 'MEDICAL'],
    typical_casualty_modifier: 0.25,
    has_frontage: false
  }
};

// ============================================================================
// MODERN WARFARE ADJUSTMENTS
// Ukraine 2022-2024 validated multipliers
// ============================================================================

export const MODERN_WARFARE_ADJUSTMENTS = {
  // Unit category adjustments (apply to base modifier)
  ARTILLERY: 1.3,      // Counter-battery much more lethal
  LOGISTICS: 1.2,      // Deep strike targeting logistics
  AVIATION: 0.6,       // Limited use due to air defense
  SIGNAL: 1.1,         // EW targeting
  MEDICAL: 1.2,        // Collateral damage despite protected status
  COMBAT: 1.0,         // Baseline for combat units
  
  // Threat type adjustments
  DRONE_WARFARE: {
    armor_units: 1.5,   // FPV drones devastating to armor
    artillery: 1.6,     // Lancet targeting artillery
    logistics: 1.4,     // Drone convoy attacks
    infantry: 1.2       // Drones less effective vs dispersed infantry
  },
  
  ARTILLERY_EFFECT: {
    infantry: 1.4,      // Very effective vs infantry
    mechanized: 1.2,    // Protected but still vulnerable
    armor: 0.9,         // Armor provides protection
    artillery: 1.8      // Counter-battery
  },
  
  notes: [
    'Ukraine data shows drone warfare changes casualty patterns',
    'Counter-battery fire much more accurate with drone spotting',
    'Deep strike capability increases rear area casualties',
    'Medical marking provides limited protection'
  ]
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get unit type object by key
 * @param {string} typeKey - Unit type key
 * @returns {Object} Unit type configuration
 */
/**
 * Sub-unit types generated by the force builder that are not selectable battalion types (2026-10-11).
 * MILITARY_INTELLIGENCE is added to every INFANTRY brigade of every national standard; it was in neither BATTALION_TYPES nor
 * UNIT_TYPES, so the calculator stopped with 'Unknown unit type' on any infantry brigade built in Tab 1.
 * The modifier equals SIGNAL (0.4): EDUCATED GUESS (headquarters-area support unit, no data).
 */
export const SUBUNIT_ONLY_TYPES = {
  MILITARY_INTELLIGENCE: {
    value: 'MILITARY_INTELLIGENCE',
    label: 'Military Intelligence (generated sub-unit)',
    casualty_modifier: 0.3,
    status: 'EDUCATED_GUESS',
    basis: 'Same as SIGNAL (0.3 since 2026-10-11); no data.'
  }
};

export const getUnitType = (typeKey) => {
  return UNIT_TYPES[typeKey] || UNIT_TYPES.INFANTRY;
};

/**
 * Get battalion type object by key
 * @param {string} typeKey - Battalion type key
 * @returns {Object} Battalion type configuration
 */
export const getBattalionType = (typeKey) => {
  return BATTALION_TYPES[typeKey] || BATTALION_TYPES.INFANTRY;
};

/**
 * Get casualty modifier for a unit type
 * @param {string} typeKey - Unit type key
 * @returns {number} Casualty modifier
 */
export const getCasualtyModifier = (typeKey) => {
  const unitType = UNIT_TYPES[typeKey] || BATTALION_TYPES[typeKey];
  return unitType?.casualty_modifier || 1.0;
};

/**
 * Get Ukraine-validated casualty modifier
 * @param {string} typeKey - Unit type key
 * @returns {Object} Ukraine casualty data
 */
export const getUkraineCasualtyData = (typeKey) => {
  const unitType = UNIT_TYPES[typeKey] || BATTALION_TYPES[typeKey];
  return unitType?.ukraine_data || {
    base_rate: 1.0,
    drone_vulnerability: 1.0,
    artillery_vulnerability: 1.0,
    wia_kia_ratio: 3.0
  };
};

/**
 * Calculate unit casualties with modern warfare modifiers
 * @param {string} unitType - Unit type key
 * @param {number} strength - Unit strength
 * @param {number} dailyRate - Base daily casualty rate
 * @param {Object} threatEnv - Threat environment (drone, artillery levels)
 * @returns {Object} Casualty estimate
 */
export const calculateUnitCasualties = (unitType, strength, dailyRate, threatEnv = {}) => {
  const ukraineData = getUkraineCasualtyData(unitType);
  
  // Apply modifiers
  let modifiedRate = dailyRate * ukraineData.base_rate;
  
  // Drone threat
  if (threatEnv.droneLevel === 'HIGH' || threatEnv.droneLevel === 'EXTREME') {
    modifiedRate *= ukraineData.drone_vulnerability;
  }
  
  // Artillery threat
  if (threatEnv.artilleryLevel === 'HIGH' || threatEnv.artilleryLevel === 'EXTREME') {
    modifiedRate *= ukraineData.artillery_vulnerability;
  }
  
  // Calculate casualties
  const totalCasualties = Math.round(strength * modifiedRate);
  const wiaKiaRatio = ukraineData.wia_kia_ratio;
  
  const kia = Math.round(totalCasualties / (1 + wiaKiaRatio));
  const wia = totalCasualties - kia;
  
  return {
    total: totalCasualties,
    kia,
    wia,
    rate: modifiedRate,
    modifiers_applied: {
      base: ukraineData.base_rate,
      drone: threatEnv.droneLevel ? ukraineData.drone_vulnerability : 1.0,
      artillery: threatEnv.artilleryLevel ? ukraineData.artillery_vulnerability : 1.0
    }
  };
};

/**
 * Get battalion composition template for a brigade type
 * @param {string} brigadeType - Brigade type key
 * @returns {Object} Battalion composition
 */
export const getBattalionComposition = (brigadeType) => {
  // 2026-10-24 (naming audit step 4): a brigade type without a composition throws (it took DEFAULT, 3 infantry + artillery +
  // logistics, silently). DEFAULT stays in the table as the composition of an HQ placeholder, not as a fallback.
  return lookupByUnitType(BATTALION_COMPOSITION_BY_BRIGADE_TYPE, brigadeType, 'getBattalionComposition');
};

/**
 * Expand battalion composition into individual battalion objects
 * @param {string} brigadeType - Brigade type key
 * @param {string} brigadeId - Parent brigade ID
 * @returns {Array} Array of battalion objects
 */
export const expandBattalionComposition = (brigadeType, brigadeId) => {
  const composition = getBattalionComposition(brigadeType);
  const battalions = [];
  let index = 0;
  
  composition.template.forEach(({ type, count }) => {
    for (let i = 0; i < count; i++) {
      const battalionType = BATTALION_TYPES[type];
      battalions.push({
        id: `${brigadeId}_bn_${index}`,
        name: `${index + 1} ${battalionType?.label || type}`,
        unit_type: type,
        parent_brigade_id: brigadeId,
        ...battalionType
      });
      index++;
    }
  });
  
  return battalions;
};

/**
 * Calculate weighted casualty modifier for mixed battalion composition
 * @param {Array} battalions - Array of battalion objects
 * @returns {number} Weighted average casualty modifier
 */
export const calculateCompositionCasualties = (battalions) => {
  if (!battalions || battalions.length === 0) return 1.0;
  
  let totalStrength = 0;
  let weightedModifier = 0;
  
  battalions.forEach(bn => {
    const type = BATTALION_TYPES[bn.unit_type];
    const strength = bn.strength || type?.typical_strength || 800;
    const modifier = type?.casualty_modifier || 1.0;
    
    totalStrength += strength;
    weightedModifier += strength * modifier;
  });
  
  return totalStrength > 0 ? weightedModifier / totalStrength : 1.0;
};

/**
 * Get medical requirements for unit type
 * @param {string} typeKey - Unit type key
 * @returns {Object} Medical requirements
 */
export const getMedicalRequirements = (typeKey) => {
  const unitType = UNIT_TYPES[typeKey] || BATTALION_TYPES[typeKey];
  return unitType?.medical || {
    ambulances: 4,
    medevac_organic: false
  };
};

/**
 * Get all unit types as array for dropdown
 * @returns {Array} Unit type options
 */
export const getUnitTypeOptions = () => {
  return Object.entries(UNIT_TYPES).map(([key, value]) => ({
    value: key,
    label: value.label,
    icon: value.icon,
    color: value.color,
    category: value.category
  }));
};

/**
 * Get all battalion types as array for dropdown
 * @returns {Array} Battalion type options
 */
export const getBattalionTypeOptions = () => {
  return Object.entries(BATTALION_TYPES).map(([key, value]) => ({
    value: key,
    label: value.label,
    icon: value.icon,
    category: value.category,
    casualty_modifier: value.casualty_modifier
  }));
};

/**
 * Get unit types by category
 * @param {string} categoryKey - Category key
 * @returns {Array} Filtered unit types
 */
export const getUnitTypesByCategory = (categoryKey) => {
  const category = UNIT_TYPE_CATEGORIES[categoryKey];
  if (!category) return [];
  
  return category.types.map(typeKey => ({
    value: typeKey,
    ...UNIT_TYPES[typeKey]
  }));
};

/**
 * Get unit types with frontage (combat units)
 * @returns {Array} Combat unit types
 */
export const getFrontageUnitTypes = () => {
  return Object.entries(UNIT_TYPES)
    .filter(([_, type]) => type.has_frontage)
    .map(([key, type]) => ({ value: key, ...type }));
};

/**
 * Get support unit types (no frontage)
 * @returns {Array} Support unit types
 */
export const getSupportUnitTypes = () => {
  return Object.entries(UNIT_TYPES)
    .filter(([_, type]) => !type.has_frontage)
    .map(([key, type]) => ({ value: key, ...type }));
};

/**
 * Check if unit type is valid
 * @param {string} typeKey - Unit type key
 * @returns {boolean} True if valid
 */
export const isValidUnitType = (typeKey) => {
  return typeKey in UNIT_TYPES || typeKey in BATTALION_TYPES;
};

/**
 * Get typical position for unit type
 * @param {string} typeKey - Unit type key
 * @returns {number} Position in km from FEBA
 */
export const getTypicalPosition = (typeKey) => {
  const unitType = UNIT_TYPES[typeKey] || BATTALION_TYPES[typeKey];
  return unitType?.typical_position_km || 0;
};

/**
 * Get complete defaults for a unit type
 * @param {string} typeKey - Unit type key
 * @returns {Object} Unit type defaults
 */
export const getUnitTypeDefaults = (typeKey) => {
  const unitType = UNIT_TYPES[typeKey] || BATTALION_TYPES[typeKey];
  
  if (!unitType) {
    return {
      typical_position_km: 0,
      default_posture: 'DELIBERATE_DEFENSE',
      default_mission: 'DEFENSE',
      operational_state: 'SUPPORT_ACTIVE',
      casualty_modifier: 1.0,
      has_frontage: false
    };
  }
  
  return {
    typical_position_km: unitType.typical_position_km || 0,
    default_posture: unitType.default_posture || 'DELIBERATE_DEFENSE',
    default_mission: unitType.default_mission || 'DEFENSE',
    operational_state: unitType.operational_state || 'SUPPORT_ACTIVE',
    casualty_modifier: unitType.casualty_modifier || 1.0,
    has_frontage: unitType.has_frontage || false
  };
};

/**
 * Apply unit type defaults to a unit object
 * @param {Object} unit - Unit object
 * @param {string} typeKey - Unit type key
 * @returns {Object} Unit with defaults applied
 */
export const applyUnitTypeDefaults = (unit, typeKey) => {
  const defaults = getUnitTypeDefaults(typeKey);
  
  return {
    ...unit,
    unit_type: typeKey,
    position_from_feba: defaults.typical_position_km,
    posture: defaults.default_posture,
    mission_type: defaults.default_mission,
    operational_state: defaults.operational_state,
    casualty_modifier: defaults.casualty_modifier,
    has_frontage: defaults.has_frontage
  };
};

/**
 * Check if unit type is a medical provider brigade
 * @param {string} unitTypeKey - Unit type key
 * @returns {boolean} True if medical provider
 */
export const isMedicalBrigade = (unitTypeKey) => {
  const unitType = UNIT_TYPES[unitTypeKey];
  return unitType?.is_medical_provider === true;
};

/**
 * Get operational state configuration
 * @param {string} stateKey - Operational state key
 * @returns {Object} Operational state configuration
 */
export const getOperationalState = (stateKey) => {
  return OPERATIONAL_STATES[stateKey] || OPERATIONAL_STATES.SUPPORT_ACTIVE;
};

/**
 * Get all operational states for dropdown
 * @returns {Array} Operational state options
 */
export const getOperationalStatesArray = () => {
  return Object.values(OPERATIONAL_STATES);
};

/**
 * Apply modern warfare adjustment to unit type modifier
 * @param {string} unitTypeKey - Unit type key
 * @returns {number} Modern warfare adjusted modifier
 */
export const getModernWarfareModifier = (unitTypeKey) => {
  const unitType = UNIT_TYPES[unitTypeKey];
  if (!unitType) return 1.0;
  
  // Map unit types to modern warfare categories
  const categoryMap = {
    ARTILLERY: 'ARTILLERY',
    LOGISTICS: 'LOGISTICS',
    AVIATION: 'AVIATION',
    SIGNAL: 'SIGNAL',
    MEDICAL: 'MEDICAL'
  };
  
  const category = categoryMap[unitTypeKey];
  if (category && MODERN_WARFARE_ADJUSTMENTS[category]) {
    return MODERN_WARFARE_ADJUSTMENTS[category];
  }
  
  // Combat units
  if (unitType.has_frontage) {
    return MODERN_WARFARE_ADJUSTMENTS.COMBAT;
  }
  
  return 1.0;
};

/**
 * Validate unit type configuration
 * @param {Object} config - Configuration to validate
 * @returns {Object} Validation result
 */
export const validateUnitTypeConfiguration = (config) => {
  const errors = [];
  const warnings = [];
  
  if (!config.unit_type || !isValidUnitType(config.unit_type)) {
    errors.push('Invalid or missing unit type');
  }
  
  if (config.strength) {
    const unitType = UNIT_TYPES[config.unit_type] || BATTALION_TYPES[config.unit_type];
    if (unitType?.strength_range) {
      if (config.strength < unitType.strength_range.min) {
        warnings.push(`Strength ${config.strength} below typical minimum ${unitType.strength_range.min}`);
      }
      if (config.strength > unitType.strength_range.max) {
        warnings.push(`Strength ${config.strength} above typical maximum ${unitType.strength_range.max}`);
      }
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
};

// ============================================================================
// EDUCATIONAL CONTENT
// ============================================================================

export const UNIT_TYPE_EDUCATIONAL = {
  key_principles: [
    'Unit type determines base casualty modifier',
    'Combat units (frontage) have highest casualties',
    'CSS units have lowest casualties but are not immune',
    'Ukraine data shows all rear areas now contested'
  ],
  
  ukraine_lessons: [
    'FPV drones devastate armor - 1.5x modifier',
    'Counter-battery fire very accurate - artillery 1.8x modifier',
    'Medical markings provide limited protection',
    'Deep strikes target logistics - nowhere truly safe',
    'Infantry has highest absolute casualties but best survival ratio'
  ],
  
  common_errors: [
    'Assuming rear areas are safe',
    'Using pre-drone era casualty factors',
    'Ignoring medical marking targeting',
    'Not adjusting for artillery accuracy improvements'
  ]
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named default export (fixes lint warning)
const unitTypesConstants = {
  UNIT_TYPES,
  BATTALION_TYPES,
  BATTALION_COMPOSITION_BY_BRIGADE_TYPE,
  UNIT_TYPE_CATEGORIES,
  MODERN_WARFARE_ADJUSTMENTS,
  OPERATIONAL_STATES,
  getUnitType,
  getBattalionType,
  getCasualtyModifier,
  getUkraineCasualtyData,
  calculateUnitCasualties,
  getBattalionComposition,
  expandBattalionComposition,
  calculateCompositionCasualties,
  getMedicalRequirements,
  getUnitTypeOptions,
  getBattalionTypeOptions,
  getUnitTypesByCategory,
  getFrontageUnitTypes,
  getSupportUnitTypes,
  isValidUnitType,
  getTypicalPosition,
  getUnitTypeDefaults,
  applyUnitTypeDefaults,
  isMedicalBrigade,
  getOperationalState,
  getOperationalStatesArray,
  getModernWarfareModifier,
  validateUnitTypeConfiguration,
  UNIT_TYPE_EDUCATIONAL
};

// ============================================================================
// OPERATIONAL CONSTRAINTS BY UNIT CATEGORY
// Added: February 2, 2026 - Prevents illogical state/posture combinations
// ============================================================================

/**
 * Defines which operational postures and states are allowed for each unit category.
 * Used by Tab1 to:
 * 1. Filter posture/state dropdowns (only show valid options)
 * 2. Validate current settings (show errors for invalid combinations)
 * 3. Auto-apply correct defaults when unit type changes
 */
export const OPERATIONAL_CONSTRAINTS = {
  // COMBAT UNITS: Can engage in offensive/defensive operations
  COMBAT: {
    allowed_postures: [
      // Offensive
      'DELIBERATE_ATTACK', 'HASTY_ATTACK', 'EXPLOITATION', 'PURSUIT', 'MOVEMENT_TO_CONTACT',
      // Defensive
      'DELIBERATE_DEFENSE', 'HASTY_DEFENSE', 'AREA_DEFENSE', 'MOBILE_DEFENSE',
      // Retrograde
      'DELAY', 'WITHDRAWAL', 'RETIREMENT',
      // Special
      'RIVER_CROSSING', 'AIRBORNE_OPERATION',
      // Reserve
      'RESERVE', 'RECONSTITUTION'
    ],
    allowed_states: [
      'COMBAT_ENGAGED',    // Actively fighting
      'SUPPORT_ACTIVE',    // Ready but not currently engaged
      'TRUE_RESERVE'       // Resting/regenerating
    ],
    description: 'Combat units can conduct offensive, defensive, and retrograde operations'
  },
  
  // SUPPORT UNITS: Provide direct combat support but don't hold frontage
  // Examples: Artillery, Engineers, Air Defense, Signal
  SUPPORT: {
    allowed_postures: [
      // Can support offensive/defensive operations
      'DELIBERATE_ATTACK', 'HASTY_ATTACK',
      'DELIBERATE_DEFENSE', 'HASTY_DEFENSE', 'AREA_DEFENSE', 'MOBILE_DEFENSE',
      // Can conduct specialized operations
      'RIVER_CROSSING',  // Engineers
      'DELAY', 'WITHDRAWAL',  // Can support retrograde
      // Non-combat
      'SUPPORT_OPERATIONS', 'RESERVE', 'RECONSTITUTION', 'NON_COMBAT'
    ],
    allowed_states: [
      'COMBAT_ENGAGED',    // Artillery under fire, engineers in breach
      'SUPPORT_ACTIVE',    // Typical state - providing support
      'TRUE_RESERVE'       // Resting/regenerating
    ],
    description: 'Support units provide combat support but are not primary maneuver elements'
  },
  
  // CSS UNITS: Combat Service Support - pure support, no combat role
  // Examples: Logistics, Medical, Maintenance, Transport, Supply, Sustainment
  CSS: {
    allowed_postures: [
      'SUPPORT_OPERATIONS',  // Primary posture
      'RESERVE',             // In reserve
      'RECONSTITUTION',      // Recovering/rebuilding
      'NON_COMBAT'          // Administrative/training
      // CANNOT: Any offensive/defensive operations
    ],
    allowed_states: [
      'SUPPORT_ACTIVE',      // Typical state - providing support
      'TRUE_RESERVE'         // Resting/regenerating
      // CANNOT: COMBAT_ENGAGED (CSS units don't fight)
    ],
    description: 'CSS units provide logistics/medical support and do not conduct combat operations'
  },
  
  // HQ UNITS: Headquarters elements (CORPS/DIVISION)
  // Command and control only, do not conduct operations directly
  HQ: {
    allowed_postures: [
      'SUPPORT_OPERATIONS',  // Typical posture (commanding subordinates)
      'RESERVE',             // In reserve
      'RECONSTITUTION',      // Recovering/rebuilding
      'NON_COMBAT'          // Administrative/training
      // HQ inherits broader operational postures from echelon doctrine, not unit type
      // But for unit-specific settings, these are the only valid choices
    ],
    allowed_states: [
      'SUPPORT_ACTIVE',      // Command and control active
      'TRUE_RESERVE'         // Resting/regenerating
      // CANNOT: COMBAT_ENGAGED (HQ doesn't fight directly)
    ],
    description: 'Headquarters units provide command and control but do not conduct direct combat operations'
  }
};

/**
 * Get operational constraints for a specific unit type
 * @param {string} unitType - Unit type key (e.g., 'MECHANIZED', 'SUSTAINMENT')
 * @returns {Object} Constraints for this unit type
 */
export function getOperationalConstraints(unitType) {
  const unit = UNIT_TYPES[unitType];
  if (!unit) {
    console.warn(`Unknown unit type: ${unitType}`);
    return OPERATIONAL_CONSTRAINTS.COMBAT; // Safest default
  }
  
  return OPERATIONAL_CONSTRAINTS[unit.category] || OPERATIONAL_CONSTRAINTS.COMBAT;
}

/**
 * Validate if a posture is allowed for a unit type
 * @param {string} unitType - Unit type key
 * @param {string} posture - Posture value to validate
 * @returns {boolean} True if posture is allowed
 */
export function isPostureAllowedForUnit(unitType, posture) {
  const constraints = getOperationalConstraints(unitType);
  return constraints.allowed_postures.includes(posture);
}

/**
 * Validate if a state is allowed for a unit type
 * @param {string} unitType - Unit type key
 * @param {string} state - State value to validate
 * @returns {boolean} True if state is allowed
 */
export function isStateAllowedForUnit(unitType, state) {
  const constraints = getOperationalConstraints(unitType);
  return constraints.allowed_states.includes(state);
}

/**
 * Get validation error for invalid posture/state combination
 * @param {string} unitType - Unit type key
 * @param {string} posture - Current posture
 * @param {string} state - Current state
 * @param {string} mode - 'STANDARD' or 'EXPERT' (affects severity)
 * @returns {Object|null} Error object or null if valid
 */
export function validateUnitOperationalSettings(unitType, posture, state, mode = 'STANDARD') {
  const unit = UNIT_TYPES[unitType];
  if (!unit) return null;
  
  const constraints = getOperationalConstraints(unitType);
  
  // Determine severity based on mode
  const severity = mode === 'STANDARD' ? 'error' : 'warning';
  const action = mode === 'STANDARD' ? 'This combination is blocked in Standard Mode.' : 'Expert Mode: This combination is allowed but not recommended.';
  
  // Check posture
  if (posture && !constraints.allowed_postures.includes(posture)) {
    return {
      isValid: mode !== 'STANDARD', // Valid in expert mode (with warning)
      severity: severity,
      field: 'posture',
      message: `Invalid posture for ${unit.label}`,
      explanation: `${unit.category} units cannot be in ${posture.replace(/_/g, ' ')}. ${constraints.description}`,
      suggestion: `Change posture to: ${constraints.allowed_postures.slice(0, 3).join(', ')}, etc. ${action}`
    };
  }
  
  // Check state
  if (state && !constraints.allowed_states.includes(state)) {
    return {
      isValid: mode !== 'STANDARD',
      severity: severity,
      field: 'state',
      message: `Invalid operational state for ${unit.label}`,
      explanation: `${unit.category} units cannot be ${state.replace(/_/g, ' ')}. ${constraints.description}`,
      suggestion: `Change state to: ${constraints.allowed_states.join(' or ')}. ${action}`
    };
  }
  
  return null; // Valid
}

/**
 * Translate combat postures to support postures for CSS units
 * When CSS units inherit parent combat posture, translate to appropriate support posture
 * @param {string} unitType - Unit type key
 * @param {string} posture - Posture to translate
 * @returns {string} Translated posture (or original if valid)
 */
export function translatePostureForUnitType(unitType, posture) {
  const constraints = getOperationalConstraints(unitType);
  
  // If posture is already valid for this unit type, no translation needed
  if (constraints.allowed_postures.includes(posture)) {
    return posture;
  }
  
  // CSS units: translate combat postures → SUPPORT_OPERATIONS
  if (UNIT_TYPES[unitType]?.category === 'CSS') {
    const combatPostures = [
      'DELIBERATE_ATTACK', 'HASTY_ATTACK', 'EXPLOITATION', 'PURSUIT', 'MOVEMENT_TO_CONTACT',
      'DELIBERATE_DEFENSE', 'HASTY_DEFENSE', 'AREA_DEFENSE', 'MOBILE_DEFENSE',
      'DELAY', 'WITHDRAWAL', 'RETIREMENT', 'RIVER_CROSSING', 'AIRBORNE_OPERATION'
    ];
    
    if (combatPostures.includes(posture)) {
      return 'SUPPORT_OPERATIONS'; // CSS supports combat but doesn't conduct it
    }
  }
  
  // If we can't translate, return first allowed posture as fallback
  return constraints.allowed_postures[0] || posture;
}

export default unitTypesConstants;
